// src/components/clients/CuentasAbiertasTab.js
import React, { useState, useEffect, useCallback } from 'react';
import { 
    Table, 
    Button, 
    Badge,
    Tag, 
    Space, 
    DatePicker, 
    message, 
    Card, 
    Statistic, 
    Select,
    Checkbox,
    Switch,
    Typography,
    Tooltip,
    Empty,
    Modal,
    Input,
    Popconfirm,
    Spin
} from 'antd';
import { 
    ReloadOutlined, 
    DollarOutlined, 
    PlusOutlined,
    FileTextOutlined,
    ShoppingCartOutlined,
    RollbackOutlined,
    WalletOutlined,
    UserAddOutlined,
    SwapOutlined,
    StopOutlined,
    ExclamationCircleOutlined,
    CloseCircleOutlined,
    InfoCircleOutlined,
    CalculatorOutlined,
    PrinterOutlined,
    EyeOutlined,
    RiseOutlined,
    FallOutlined,
    SyncOutlined,
    EditOutlined,
    InboxOutlined,
    CheckCircleOutlined,
    BankOutlined,
    HighlightOutlined,
    FileDoneOutlined
} from '@ant-design/icons';
import { runningAccountService } from '../../services/runningAccountService';
import { useCart } from '../../context/CartContext';
import AgregarClienteRunningAccountModal from './AgregarClienteRunningAccountModal';
import EditarClienteRunningAccountModal from './EditarClienteRunningAccountModal';
import AgregarMovimientoModal from './AgregarMovimientoModal';
import FacturarCuentaAbiertaModal from '../runningaccount/FacturarCuentaAbiertaModal';
import LiquidarCargoModal from './LiquidarCargoModal';
import dayjs from 'dayjs';

const isDemoMode = process.env.REACT_APP_DEMO_MODE === 'true';

const { RangePicker } = DatePicker;
const { Option } = Select;
const { Text } = Typography;







const CuentasAbiertasTab = () => {
    // Cart context
    const { clearCart, toggleItemInCart, updateItemQuantity, updateItemSplitter } = useCart();

    // State
    const [clients, setClients] = useState([]);
    const [selectedClient, setSelectedClient] = useState(null);
    const [movements, setMovements] = useState([]);
    const [loading, setLoading] = useState(false);
    const [loadingClients, setLoadingClients] = useState(false);
    const [selectedRowKeys, setSelectedRowKeys] = useState([]);
    const [agregarClienteModalVisible, setAgregarClienteModalVisible] = useState(false);
    const [editarClienteModalVisible, setEditarClienteModalVisible] = useState(false);
    const [agregarMovimientoModalVisible, setAgregarMovimientoModalVisible] = useState(false);
    const [facturarModalVisible, setFacturarModalVisible] = useState(false);
    const [selectedItemsForInvoice, setSelectedItemsForInvoice] = useState([]);
    
    // Ver Movimiento (items detail) state
    const [verMovimientoModalVisible, setVerMovimientoModalVisible] = useState(false);
    const [movimientoToView, setMovimientoToView] = useState(null);

    // Void movement state
    const [voidModalVisible, setVoidModalVisible] = useState(false);
    const [movementToVoid, setMovementToVoid] = useState(null);
    const [voidReason, setVoidReason] = useState('');
    const [voidLoading, setVoidLoading] = useState(false);
    
    // Pickup management state
    const [pickupModalVisible, setPickupModalVisible] = useState(false);
    const [movementToPickup, setMovementToPickup] = useState(null);
    const [pickupData, setPickupData] = useState({ pickedUpBy: '', affectsStock: true });
    const [pickupLoading, setPickupLoading] = useState(false);
    
    // Sync-Debe state
    const [syncConfirmVisible, setSyncConfirmVisible] = useState(false);
    const [movementToSync, setMovementToSync] = useState(null); // { id, debe, updatedDebe, deviationDebe, descripcion }
    const [syncLoading, setSyncLoading] = useState(false);
    // NOTE: synced-movement detection is data-driven via m.notasInternas ("DEBE ACTUALIZADO...")
    // which the backend writes on sync — no client-side state needed.

    // Archive state
    const [showArchivedMovements, setShowArchivedMovements] = useState(false); // default: hide archived
    const [archivingLoading, setArchivingLoading] = useState(false);
    const [showVoidedMovements, setShowVoidedMovements] = useState(false); // default: hide voided

    // Settlement (liquidación) state
    const [liquidarModalVisible, setLiquidarModalVisible] = useState(false);
    const [selectedCargosForSettlement, setSelectedCargosForSettlement] = useState([]);
    const [settlementLoading, setSettlementLoading] = useState(false);
    const [paidTogglingId, setPaidTogglingId] = useState(null);

    // Expanded rows — controlled so we can auto-expand paid CARGOs
    const [expandedRowKeys, setExpandedRowKeys] = useState([]);

    // Aggregate statistics state (for when no client is selected)
    const [aggregateStats, setAggregateStats] = useState(null);
    const [loadingStats, setLoadingStats] = useState(false);
    const [balanceBreakdownVisible, setBalanceBreakdownVisible] = useState(false);
    const [saldoInfoVisible, setSaldoInfoVisible] = useState(false);
    
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 20,
        total: 0,
    });
    const [filters, setFilters] = useState({
        dateFrom: null,
        dateTo: null,
    });

    // Fetch clients on mount
    useEffect(() => {
        fetchClients();
    }, []);

    // Fetch aggregate statistics when no client is selected
    useEffect(() => {
        if (!selectedClient) {
            fetchAggregateStatistics();
        }
    }, [selectedClient]);

    // Re-fetch movements when "show archived" toggle changes
    useEffect(() => {
        if (selectedClient) {
            fetchMovements(selectedClient.id || selectedClient.documento_nro, 1, pagination.pageSize);
        }
    }, [showArchivedMovements]); // eslint-disable-line

    // Re-fetch movements when "show voided" toggle changes
    useEffect(() => {
        if (selectedClient) {
            fetchMovements(selectedClient.id || selectedClient.documento_nro, 1, pagination.pageSize);
        }
    }, [showVoidedMovements]); // eslint-disable-line

    // Fetch aggregate statistics for all clients
    const fetchAggregateStatistics = async () => {
        setLoadingStats(true);
        try {
            const response = await runningAccountService.getAggregateStatistics();
            if (response.success && response.data) {
                // Map API response to expected format
                setAggregateStats(response.data);
                console.log('[CuentasAbiertas] Aggregate statistics loaded:', response.data);
            } else if (response.success && response.statistics) {
                setAggregateStats(response.statistics);
                console.log('[CuentasAbiertas] Aggregate statistics loaded:', response.statistics);
            } else {
                console.warn('[CuentasAbiertas] No statistics data returned from API');
                setAggregateStats(null);
            }
        } catch (error) {
            console.error('[CuentasAbiertas] Error loading aggregate statistics:', error);
            message.error('Error al cargar estadísticas');
            setAggregateStats(null);
        } finally {
            setLoadingStats(false);
        }
    };

    // Fetch clients from API
    const fetchClients = async () => {
        setLoadingClients(true);
        try {
            const response = await runningAccountService.getClients();
            const clientList = response.clients || response || [];
            setClients(clientList);
            console.log('[CuentasAbiertas] Clients loaded:', clientList);
            if (clientList.length === 0) {
                console.log('[CuentasAbiertas] No running account clients found');
            }
        } catch (error) {
            console.error('[CuentasAbiertas] Error loading clients:', error);
            message.error('Error al cargar clientes de cuenta abierta');
            setClients([]);
        } finally {
            setLoadingClients(false);
        }
    };

    // Fetch movements for selected client
    const fetchMovements = useCallback(async (clientId, page = 1, pageSize = 20) => {
        if (!clientId) {
            setMovements([]);
            return;
        }

        setLoading(true);
        try {
            console.log('[CuentasAbiertas] Fetching movements for client:', clientId, 'page:', page, 'pageSize:', pageSize);
            // Request movements WITH items array for CARGO movements
            const response = await runningAccountService.getClientMovements(clientId, {
                ...filters,
                includeItems: true,  // ✅ Request product details
                includeArchived: showArchivedMovements,
                includeVoided: showVoidedMovements,
                page,
                pageSize
            });
            
            if (response.success) {
                // Transform movements to expected format
                const movementsList = response.movements || [];
                const transformedMovements = movementsList.map(m => {
                    // Build description from items if available
                    let descripcion = m.descripcion || '';
                    
                    // For CARGO movements with items array, build detailed description
                    if (m.tipo === 'CARGO' && m.items && m.items.length > 0) {
                        if (m.items.length === 1) {
                            // Single item - show product code and name
                            const item = m.items[0];
                            descripcion = `${item.productCode} - ${item.productName}`;
                        } else {
                            // Multiple items - show codes summary
                            const codes = m.items.slice(0, 3).map(i => i.productCode).join(', ');
                            const suffix = m.items.length > 3 ? ` (+${m.items.length - 3} más)` : '';
                            descripcion = `${m.items.length} productos: ${codes}${suffix}`;
                        }
                    } else if (m.tipo === 'CARGO' && m.tipoOrigen === 'PRODUCTS') {
                        // Fallback if no items array
                        if (m.itemsCount > 1) {
                            descripcion = `${m.itemsCount} productos agregados a cuenta`;
                        } else if (m.itemsCount === 1) {
                            descripcion = '1 producto agregado a cuenta';
                        } else {
                            descripcion = m.descripcion || 'Productos agregados a cuenta';
                        }
                    } else if (!descripcion && m.notasInternas) {
                        // For non-CARGO movements (PAGO, ADELANTO, AJUSTE), use notasInternas as description
                        descripcion = m.notasInternas;
                    } else if (!descripcion && m.tipo) {
                        // Fallback: generate description from movement type
                        const typeLabels = {
                            'PAGO': 'Pago recibido',
                            'ADELANTO': 'Adelanto / Seña',
                            'AJUSTE_CREDITO': 'Ajuste a favor',
                            'AJUSTE_DEBITO': 'Ajuste en contra',
                            'ANULACION': 'Anulación'
                        };
                        descripcion = typeLabels[m.tipo] || m.tipo;
                    }
                    
                    return {
                        id: m.id,
                        tipo: m.tipo === 'CARGO'
                            ? (m.invoiceId ? 'PRODUCTO_FACTURADO' : 'PRODUCTO')
                            : m.tipo === 'LIQUIDACION' ? 'LIQUIDACION' : 'MOVIMIENTO',
                        tipoOriginal: m.tipo, // Keep original type for void logic (CARGO, PAGO, ANULACION)
                        tipoOrigen: m.tipoOrigen,
                        fecha: m.fecha || m.fechaHora,
                        descripcion: descripcion,
                        notasInternas: m.notasInternas,
                        factura_id: m.invoiceId || null,
                        factura_nro: m.invoiceNumber || null,
                        cantidad: m.itemsCount || 1,
                        precio_unitario: m.debe || m.haber || 0,
                        valor: m.debe || m.haber || 0,
                        debe: m.debe || 0,
                        haber: m.haber || 0,
                        saldo: m.saldoAcumulado || 0,
                        status: m.status,
                        transactionId: m.transactionId,
                        metodoPago: m.metodoPago,
                        referenciaPago: m.referenciaPago,
                        // ✅ Include discount info from movement
                        descuentoPorcentaje: m.descuentoPorcentaje || 0,
                        descuentoMonto: m.descuentoMonto || 0,
                        // ✅ Include items array for CARGO movements
                        items: m.items || null,
                        // ✅ Include pickup and stock management fields
                        pickedUp: m.pickedUp || false,
                        pickedUpBy: m.pickedUpBy || null,
                        pickedUpAt: m.pickedUpAt || null,
                        affectsStock: m.affectsStock !== false, // Default true
                        stockMovementId: m.stockMovementId || null,
                        // ✅ Updated price fields (current catalog price deviation)
                        updatedDebe: m.updatedDebe ?? null,
                        deviationDebe: m.deviationDebe ?? null,
                        // ✅ Flag fields
                        isPaid: m.isPaid ?? false,
                        isArchived: m.isArchived ?? false,
                        // ✅ LIQUIDACION link: the CARGO id this LIQUIDACION settled
                        movimientoOriginalId: m.movimientoOriginalId || null
                    };
                });

                // Client-side defensive filter — backend may return archived/voided rows
                // regardless of the params; we enforce the user's toggle choice here.
                const filteredMovements = transformedMovements.filter(m => {
                    if (!showArchivedMovements && m.isArchived) return false;
                    if (!showVoidedMovements && m.status === 'VOIDED') return false;
                    return true;
                });
                
                setMovements(filteredMovements);

                // Auto-expand CARGO rows that have a paired LIQUIDACION
                const autoExpand = transformedMovements
                    .filter(m =>
                        (m.tipo === 'PRODUCTO' || m.tipo === 'PRODUCTO_FACTURADO') &&
                        m.isPaid === true &&
                        transformedMovements.some(l => l.tipo === 'LIQUIDACION' && l.movimientoOriginalId === m.id)
                    )
                    .map(m => m.id);
                setExpandedRowKeys(autoExpand);

                setPagination(prev => ({
                    ...prev,
                    current: page,
                    pageSize: pageSize,
                    total: response.pagination?.totalRecords || transformedMovements.length
                }));
                
                // Update selected client with full data from API response
                if (response.client || response.summary) {
                    setSelectedClient(prev => ({
                        ...prev,
                        // Client data from API response
                        document_number: response.client?.documentNumber || prev?.document_number,
                        document_type: response.client?.documentType || prev?.document_type,
                        business_name: response.client?.businessName || prev?.business_name,
                        tax_condition: response.client?.taxCondition || prev?.tax_condition,
                        tax_condition_code: response.client?.taxConditionCode || prev?.tax_condition_code,
                        // Summary data
                        saldo_pendiente: response.summary?.saldoActual || prev?.saldo_pendiente,
                        current_balance: response.summary?.saldoActual || prev?.current_balance,
                        totalDebe: response.summary?.totalDebe,
                        totalHaber: response.summary?.totalHaber,
                        productosSinFacturar: response.summary?.productosSinFacturar || 0,
                        // ✅ Updated price fields from new backend
                        updatedTotalDebe: response.summary?.updatedTotalDebe ?? null,
                        updatedSaldoActual: response.summary?.updatedSaldoActual ?? null,
                        saldoActualDeviation: response.summary?.saldoActualDeviation ?? null
                    }));
                }
                
                console.log('[CuentasAbiertas] Movements loaded:', transformedMovements.length);
            } else {
                console.warn('[CuentasAbiertas] Failed to load movements:', response);
                setMovements([]);
            }
        } catch (error) {
            console.error('[CuentasAbiertas] Error loading movements:', error);
            message.error('Error al cargar movimientos del cliente');
            setMovements([]);
        } finally {
            setLoading(false);
        }
    }, [filters, showArchivedMovements, showVoidedMovements]);

    // Handle client selection change
    // Load a CARGO movement's items into the cart
    const handleLoadMovementToCart = async (record) => {
        const items = record.items || [];
        const validItems = items.filter(item => item.productId);

        if (validItems.length === 0) {
            message.warning('Este cargo no tiene productos con ID registrado y no puede cargarse al carrito');
            return;
        }

        message.loading({ content: 'Cargando productos al carrito...', key: 'raLoadCart', duration: 0 });

        try {
            clearCart();

            let added = 0;
            for (const item of validItems) {
                // Convert productId to string — CartProvider matches it against the string
                // ProductId returned by the cart detail API (/apitest/postidcart).
                const productId = String(item.productId);

                toggleItemInCart(productId, true);

                const qty = parseFloat(item.quantity) || 1;
                if (qty !== 1) {
                    updateItemQuantity(productId, qty);
                }

                const splitter = parseInt(item.splitter) || 1;
                if (splitter !== 1) {
                    updateItemSplitter(productId, splitter);
                }

                added++;
            }

            message.success({
                content: `${added} producto${added !== 1 ? 's' : ''} cargado${added !== 1 ? 's' : ''} al carrito`,
                key: 'raLoadCart',
                duration: 3
            });
        } catch (err) {
            console.error('[CuentasAbiertas] Error loading movement to cart:', err);
            message.error({ content: 'Error al cargar productos al carrito', key: 'raLoadCart', duration: 4 });
        }
    };

    const handleClientChange = (clientId) => {
        const client = clients.find(c => (c.id || c.documento_nro) === clientId);
        setSelectedClient(client || null);
        setSelectedRowKeys([]);
        if (client) {
            fetchMovements(client.id || client.documento_nro);
        } else {
            setMovements([]);
        }
    };

    // Handle row selection
    const onSelectChange = (newSelectedRowKeys) => {
        setSelectedRowKeys(newSelectedRowKeys);
    };

    // ========== ARCHIVE FUNCTIONS ==========

    /**
     * Whether a movement is eligible to be archived.
     * All movements are eligible EXCEPT VOIDED ones.
     * (SP no longer requires CARGO to be paid/invoiced first.)
     */
    const canArchive = (movement) => movement.status !== 'VOIDED';

    const handleArchiveSelected = async () => {
        const eligibleIds = selectedRowKeys.filter(key => {
            const mov = movements.find(m => m.id === key);
            return mov && canArchive(mov);
        });
        const skipped = selectedRowKeys.length - eligibleIds.length;

        if (eligibleIds.length === 0) {
            message.warning('Ningún movimiento seleccionado puede ser archivado. Los movimientos anulados no son elegibles.');
            return;
        }
        if (skipped > 0) {
            message.info(`${skipped} movimiento(s) omitido(s) por no cumplir los requisitos de archivo.`);
        }

        setArchivingLoading(true);
        try {
            const result = await runningAccountService.toggleMovementsArchived(eligibleIds);
            // Patch local state with new isArchived values — no full refetch needed
            const updatedMap = Object.fromEntries(result.data.map(m => [m.movementId, m]));
            setMovements(prev => prev.map(mov =>
                updatedMap[mov.id] ? { ...mov, isArchived: updatedMap[mov.id].isArchived } : mov
            ));
            setSelectedRowKeys([]);
            const archivedCount = result.data.filter(m => m.isArchived).length;
            const unarchivedCount = result.data.length - archivedCount;
            if (archivedCount > 0 && unarchivedCount === 0) {
                message.success(`${archivedCount} movimiento(s) archivado(s).`);
            } else if (unarchivedCount > 0 && archivedCount === 0) {
                message.success(`${unarchivedCount} movimiento(s) desarchivado(s).`);
            } else {
                message.success(`${result.updatedCount} movimiento(s) actualizados.`);
            }
        } catch (err) {
            if (err?.error === 'CARGO_NOT_ELIGIBLE') {
                message.warning(err.message || 'Algunos CARGOs no son elegibles para archivar (pendientes de cobro).');
            } else if (err?.error === 'MOVEMENT_VOIDED') {
                message.error('No se puede archivar un movimiento anulado.');
            } else if (err?.error === 'MOVEMENT_NOT_FOUND') {
                message.error('Uno o más movimientos no fueron encontrados.');
            } else {
                message.error('Error al archivar movimientos.');
            }
        } finally {
            setArchivingLoading(false);
        }
    };

    /**
     * Whether a CARGO movement can be settled (liquidado).
     * Must be: tipo CARGO, not already paid, not VOIDED, not INVOICED.
     */
    const canSettle = (movement) =>
        movement.tipoOriginal === 'CARGO' &&
        !movement.isPaid &&
        movement.status !== 'VOIDED' &&
        movement.status !== 'INVOICED';

    /**
     * Open the Liquidar modal pre-populated with the given CARGO movements.
     * If no ids provided, uses currently selected settleable rows.
     */
    const openLiquidarModal = (cargoIds) => {
        const cargos = movements.filter(m => cargoIds.includes(m.id) && canSettle(m));
        if (cargos.length === 0) {
            message.warning('Ningún CARGO seleccionado es elegible para liquidar.');
            return;
        }
        setSelectedCargosForSettlement(cargos);
        setLiquidarModalVisible(true);
    };

    /**
     * Called when user confirms the settlement inside LiquidarCargoModal.
     * Calls the API, patches local state, injects returned LIQUIDACION rows.
     */
    const handleConfirmSettlement = async (cargoIds) => {
        setSettlementLoading(true);
        try {
            const result = await runningAccountService.toggleMovementsPaid(cargoIds);
            // Patch CARGO rows to isPaid = true
            const settledSet = new Set(cargoIds);
            setMovements(prev => {
                // Build new LIQUIDACION rows from result
                const newLiquidaciones = (result.liquidaciones || []).map(liq => ({
                    id: liq.id,
                    tipo: 'LIQUIDACION',
                    tipoOriginal: 'LIQUIDACION',
                    tipoOrigen: liq.tipoOrigen || '',
                    fecha: liq.fecha || liq.fechaHora,
                    descripcion: liq.descripcion || 'Liquidación de cargo',
                    notasInternas: liq.notasInternas || null,
                    factura_id: null,
                    factura_nro: null,
                    cantidad: 1,
                    precio_unitario: liq.haber || 0,
                    valor: liq.haber || 0,
                    debe: 0,
                    haber: liq.haber || 0,
                    saldo: liq.saldoAcumulado || 0,
                    status: liq.status || 'LIQUIDATED',
                    isPaid: false,
                    isArchived: false,
                    movimientoOriginalId: liq.movimientoOriginalId || liq.cargoId || null,
                    items: null,
                    pickedUp: false,
                    pickedUpBy: null,
                    pickedUpAt: null,
                    affectsStock: false,
                    stockMovementId: null,
                    updatedDebe: null,
                    deviationDebe: null,
                    descuentoPorcentaje: 0,
                    descuentoMonto: 0,
                    transactionId: liq.transactionId || null,
                    metodoPago: null,
                    referenciaPago: null,
                }));
                // Mark settled CARGOs
                const updated = prev.map(m =>
                    settledSet.has(m.id) ? { ...m, isPaid: true } : m
                );
                // Prepend LIQUIDACION rows (newest first) — deduplicate by id
                const existingIds = new Set(updated.map(m => m.id));
                const toInsert = newLiquidaciones.filter(l => !existingIds.has(l.id));
                return [...toInsert, ...updated];
            });
            setSelectedRowKeys([]);
            setLiquidarModalVisible(false);
            message.success(`${cargoIds.length} CARGO(s) liquidado(s) exitosamente.`);
        } catch (err) {
            const msg = err?.message || 'Error al liquidar movimientos.';
            message.error(msg);
        } finally {
            setSettlementLoading(false);
        }
    };

    /**
     * Toggle isPaid on a single non-CARGO movement (free toggle via Popconfirm).
     */
    const handleTogglePaidSingle = async (movement) => {
        setPaidTogglingId(movement.id);
        try {
            await runningAccountService.toggleMovementsPaid([movement.id]);
            setMovements(prev =>
                prev.map(m => m.id === movement.id ? { ...m, isPaid: !m.isPaid } : m)
            );
            message.success(movement.isPaid ? 'Movimiento marcado como pendiente.' : 'Movimiento marcado como pagado.');
        } catch (err) {
            message.error('Error al actualizar estado de pago.');
        } finally {
            setPaidTogglingId(null);
        }
    };

    // Format currency
    const formatPrice = (price) => {
        if (typeof price !== 'number' || isNaN(price)) return '$ 0,00';
        return `$ ${price.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    };

    // Format date - Extract date part only (no timezone conversion)
    // The 'fecha' field represents a business date, not a timestamp
    // So we just extract YYYY-MM-DD and format it, ignoring time/timezone
    const formatDate = (date) => {
        if (!date) return '-';
        // Extract just the date part (YYYY-MM-DD) from ISO string
        const dateStr = String(date).split('T')[0];
        if (!dateStr || !dateStr.includes('-')) return '-';
        const [year, month, day] = dateStr.split('-');
        return `${day}/${month}/${year}`;
    };

    // Get movement type display
    const getMovementTypeDisplay = (record) => {
        // CARGO movements (products)
        if (record.tipo === 'PRODUCTO_FACTURADO') {
            return (
                <Tag color="blue" icon={<FileTextOutlined />}>
                    Facturado
                </Tag>
            );
        }
        if (record.tipo === 'PRODUCTO') {
            // Show Liquidado badge when CARGO has been settled
            if (record.isPaid) {
                return (
                    <Tag color="purple" icon={<HighlightOutlined />}>
                        Liquidado
                    </Tag>
                );
            }
            return (
                <Tag color="orange" icon={<ShoppingCartOutlined />}>
                    Producto
                </Tag>
            );
        }
        // LIQUIDACION movement — purple
        if (record.tipo === 'LIQUIDACION') {
            return (
                <Tag color="purple" icon={<BankOutlined />}>
                    Liquidación
                </Tag>
            );
        }
        
        // Non-CARGO movements - check tipoOriginal for specific type
        const tipoOriginal = record.tipoOriginal || '';
        const tipoOrigen = record.tipoOrigen || '';
        
        // ANULACION (void/cancellation) - dark yellow/orange to indicate cancellation
        if (tipoOriginal === 'ANULACION' || tipoOrigen === 'VOID') {
            return (
                <Tag color="gold" icon={<CloseCircleOutlined />}>
                    Movimiento
                </Tag>
            );
        }
        
        // PAGO, ADELANTO, AJUSTE_FAVOR - green for positive/payment movements
        if (tipoOriginal === 'PAGO' || tipoOrigen === 'PAYMENT' ||
            tipoOriginal === 'ADELANTO' || tipoOrigen === 'ADVANCE' ||
            tipoOriginal === 'AJUSTE_FAVOR' || tipoOrigen === 'ADJUSTMENT_CREDIT') {
            return (
                <Tag color="green" icon={<DollarOutlined />}>
                    Movimiento
                </Tag>
            );
        }
        
        // Default for other movement types
        return (
            <Tag color="cyan" icon={<DollarOutlined />}>
                Movimiento
            </Tag>
        );
    };

    // Table columns
    const columns = [
        {
            title: 'Fecha',
            dataIndex: 'fecha',
            key: 'fecha',
            width: 110,
            sorter: (a, b) => new Date(a.fecha) - new Date(b.fecha),
            render: (fecha) => formatDate(fecha),
        },
        {
            title: 'Productos/Movimientos',
            dataIndex: 'descripcion',
            key: 'descripcion',
            ellipsis: true,
            render: (text, record) => (
                <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        {getMovementTypeDisplay(record)}
                        <Text strong>{text}</Text>
                    </div>
                    {/* Show transaction reference */}
                    {record.transactionId && (
                        <Text type="secondary" style={{ fontSize: 11, display: 'block' }}>
                            Ref: {record.transactionId}
                        </Text>
                    )}
                    {/* Show invoice number if facturado */}
                    {record.factura_nro && (
                        <Text type="secondary" style={{ fontSize: 11, display: 'block' }}>
                            Factura: {record.factura_nro}
                        </Text>
                    )}
                    {/* Show payment method for payments */}
                    {record.tipo === 'MOVIMIENTO' && record.metodoPago && (
                        <Text type="secondary" style={{ fontSize: 11, display: 'block' }}>
                            Método: {record.metodoPago} {record.referenciaPago ? `- ${record.referenciaPago}` : ''}
                        </Text>
                    )}
                </div>
            ),
        },
        {
            title: 'Cant.',
            dataIndex: 'cantidad',
            key: 'cantidad',
            width: 80,
            align: 'center',
            render: (cant, record) => (
                <span style={{ color: record.tipo === 'MOVIMIENTO' ? '#8c8c8c' : 'inherit' }}>
                    {cant}
                </span>
            ),
        },
        {
            title: 'Valor',
            dataIndex: 'valor',
            key: 'valor',
            width: 150,
            align: 'right',
            render: (valor, record) => {
                const deviation = record.deviationDebe;
                const showDeviation = record.tipoOriginal === 'CARGO'
                    && record.status === 'PENDING'
                    && deviation != null
                    && Math.abs(deviation) > 0.01;
                return (
                    <div>
                        <div>{formatPrice(valor)}</div>
                        {showDeviation && (
                            <div style={{ fontSize: 11, marginTop: 2, color: deviation > 0 ? '#fa8c16' : '#52c41a' }}>
                                {deviation > 0
                                    ? <RiseOutlined style={{ marginRight: 2 }} />
                                    : <FallOutlined style={{ marginRight: 2 }} />}
                                {deviation > 0 ? '+' : ''}{deviation.toFixed(2)}%
                                <span style={{ color: '#8c8c8c', marginLeft: 4 }}>
                                    ({formatPrice(record.updatedDebe)})
                                </span>
                            </div>
                        )}
                    </div>
                );
            },
        },
        {
            title: 'DEBE',
            dataIndex: 'debe',
            key: 'debe',
            width: 120,
            align: 'right',
            render: (debe) => (
                <span style={{ color: debe > 0 ? '#cf1322' : '#8c8c8c' }}>
                    {formatPrice(Math.abs(debe))}
                </span>
            ),
        },
        {
            title: 'HABER',
            dataIndex: 'haber',
            key: 'haber',
            width: 120,
            align: 'right',
            render: (haber) => (
                <span style={{ color: haber > 0 ? '#52c41a' : '#8c8c8c' }}>
                    {formatPrice(Math.abs(haber))}
                </span>
            ),
        },
        {
            title: 'SALDO',
            dataIndex: 'saldo',
            key: 'saldo',
            width: 130,
            align: 'right',
            render: (saldo) => (
                <Text strong style={{ color: saldo > 0 ? '#cf1322' : '#52c41a' }}>
                    {formatPrice(Math.abs(saldo))}
                </Text>
            ),
        },
        {
            title: 'Retiro',
            dataIndex: 'pickedUp',
            key: 'pickedUp',
            width: 90,
            align: 'center',
            render: (pickedUp, record) => {
                // Only show for CARGO movements (tipo === 'PRODUCTO')
                if (record.tipo !== 'PRODUCTO' || record.status === 'VOIDED') {
                    return null;
                }
                
                return (
                    <Tooltip title={pickedUp ? `Retirado${record.pickedUpBy ? ` por ${record.pickedUpBy}` : ''}` : 'Pendiente de retiro'}>
                        <Tag color={pickedUp ? 'green' : 'orange'} style={{ fontSize: 11, margin: 0 }}>
                            {pickedUp ? 'Retirado' : 'Pendiente'}
                        </Tag>
                    </Tooltip>
                );
            },
        },
        {
            title: 'Acciones',
            key: 'acciones',
            width: 180,
            align: 'center',
            fixed: 'right',
            render: (_, record) => {
                const { canVoid, reason } = canVoidMovement(record);
                
                // Don't show action for ANULACION movements
                if (record.tipoOriginal === 'ANULACION' || record.tipoOrigen === 'VOID') {
                    return null;
                }
                
                // Show voided badge for VOIDED movements
                if (record.status === 'VOIDED') {
                    return (
                        <Tag color="default" style={{ fontSize: 10 }}>
                            ANULADO
                        </Tag>
                    );
                }
                
                // Check if pickup can be toggled (CARGO movements only, not voided, not invoiced)
                const canTogglePickup = record.tipo === 'PRODUCTO' && !record.factura_id;
                
                return (
                    <Space size="small">
                        {/* Sync-Debe button — only for CARGO PENDING with price deviation */}
                        {record.tipoOriginal === 'CARGO'
                            && record.status === 'PENDING'
                            && record.deviationDebe != null
                            && Math.abs(record.deviationDebe) > 0.01 && (
                            <Tooltip title={`Actualizar DEBE al precio vigente (${record.deviationDebe > 0 ? '+' : ''}${record.deviationDebe.toFixed(2)}%)`}>
                                <Button
                                    type="text"
                                    size="small"
                                    icon={<SyncOutlined />}
                                    style={{ color: '#fa8c16' }}
                                    onClick={() => {
                                        setMovementToSync({
                                            id: record.id,
                                            debe: record.debe,
                                            updatedDebe: record.updatedDebe,
                                            deviationDebe: record.deviationDebe,
                                            descripcion: record.descripcion || record.description || `Movimiento #${record.id}`
                                        });
                                        setSyncConfirmVisible(true);
                                    }}
                                />
                            </Tooltip>
                        )}

                        {/* Ver Factura button - only for invoiced CARGO rows */}
                        {record.tipo === 'PRODUCTO_FACTURADO' && record.factura_id && (
                            <Tooltip title={`Ver factura${record.factura_nro ? ` ${record.factura_nro}` : ''}`}>
                                <Button
                                    type="text"
                                    size="small"
                                    icon={<FileDoneOutlined />}
                                    style={{ color: '#722ed1' }}
                                    onClick={() => window.open(`#/print-quote?invoiceId=${record.factura_id}`, '_blank')}
                                />
                            </Tooltip>
                        )}

                        {/* Ver Productos button - only for pending CARGO movements with items */}
                        {record.tipo === 'PRODUCTO' && record.items && record.items.length > 0 && (
                            <Tooltip title="Ver productos del cargo">
                                <Button
                                    type="text"
                                    size="small"
                                    icon={<EyeOutlined />}
                                    style={{ color: '#1890ff' }}
                                    onClick={() => {
                                        setMovimientoToView(record);
                                        setVerMovimientoModalVisible(true);
                                    }}
                                />
                            </Tooltip>
                        )}

                        {/* Ver Detalle button - only for MOVIMIENTO records */}
                        {record.tipo === 'MOVIMIENTO' && (
                            <Tooltip title="Ver detalle del movimiento">
                                <Button
                                    type="text"
                                    size="small"
                                    icon={<EyeOutlined />}
                                    style={{ color: '#1890ff' }}
                                    onClick={() => {
                                        setMovimientoToView(record);
                                        setVerMovimientoModalVisible(true);
                                    }}
                                />
                            </Tooltip>
                        )}

                        {/* Pickup toggle button - only for CARGO movements */}
                        {canTogglePickup && (
                            <Tooltip title={record.pickedUp ? 'Cambiar estado de retiro' : 'Marcar como retirado'}>
                                <Button
                                    type="text"
                                    size="small"
                                    icon={<RollbackOutlined />}
                                    style={{ color: record.pickedUp ? '#52c41a' : '#fa8c16' }}
                                    onClick={() => {
                                        setMovementToPickup(record);
                                        setPickupData({ 
                                            pickedUpBy: record.pickedUpBy || '', 
                                            affectsStock: true 
                                        });
                                        setPickupModalVisible(true);
                                    }}
                                />
                            </Tooltip>
                        )}

                        {/* Cargar en Carrito button - only for CARGO movements with product items */}
                        {(record.tipo === 'PRODUCTO' || record.tipo === 'PRODUCTO_FACTURADO') && record.items && record.items.some(i => i.productId) && (
                            <Tooltip title="Cargar en Carrito">
                                <Button
                                    type="text"
                                    size="small"
                                    icon={<ShoppingCartOutlined />}
                                    style={{ color: '#52c41a' }}
                                    onClick={() => handleLoadMovementToCart(record)}
                                />
                            </Tooltip>
                        )}

                        {/* Liquidar CARGO button — settle a pending CARGO with available credit */}
                        {canSettle(record) && (
                            <Tooltip title="Liquidar este CARGO usando el crédito del cliente">
                                <Button
                                    type="text"
                                    size="small"
                                    icon={<HighlightOutlined />}
                                    style={{ color: '#cf1322' }}
                                    onClick={() => openLiquidarModal([record.id])}
                                />
                            </Tooltip>
                        )}

                        {/* isPaid free-toggle — only for non-CARGO, non-ANULACION, non-VOIDED movimientos */}
                        {record.tipo === 'MOVIMIENTO' && record.status !== 'VOIDED' && (
                            <Popconfirm
                                title={record.isPaid
                                    ? '¿Marcar este movimiento como pendiente de pago?'
                                    : '¿Marcar este movimiento como pagado?'}
                                description={record.isPaid
                                    ? 'El movimiento volverá a figurar como pendiente.'
                                    : 'Esto indica que el pago fue recibido y está registrado.'}
                                onConfirm={() => handleTogglePaidSingle(record)}
                                okText="Confirmar"
                                cancelText="Cancelar"
                            >
                                <Tooltip title={record.isPaid ? 'Pagado — click para revertir' : 'Marcar como pagado'}>
                                    <Button
                                        type="text"
                                        size="small"
                                        loading={paidTogglingId === record.id}
                                        icon={<CheckCircleOutlined />}
                                        style={{ color: record.isPaid ? '#52c41a' : '#8c8c8c' }}
                                    />
                                </Tooltip>
                            </Popconfirm>
                        )}

                        {/* Void button */}
                        <Tooltip title={canVoid ? 'Anular movimiento' : reason}>
                            <Button
                                type="text"
                                size="small"
                                danger
                                icon={<StopOutlined />}
                                disabled={!canVoid}
                                onClick={() => handleOpenVoidModal(record)}
                            />
                        </Tooltip>
                    </Space>
                );
            },
        },
    ];

    // Row selection config
    const rowSelection = {
        selectedRowKeys,
        onChange: onSelectChange,
        getCheckboxProps: (record) => {
            // VOIDED movements are never selectable
            if (record.status === 'VOIDED') {
                return { disabled: true, name: record.id };
            }
            // All non-VOIDED rows are selectable:
            // - MOVIMIENTO rows can be archive-toggled
            // - PRODUCTO rows can be invoiced OR archived
            return { disabled: false, name: record.id };
        },
    };

    // Use summary statistics from API (excludes VOIDED/ANULACION movements)
    // Fall back to calculating from movements only if API summary not available
    const totalDebe = selectedClient?.totalDebe ?? movements.reduce((sum, m) => sum + (m.debe || 0), 0);
    const totalHaber = selectedClient?.totalHaber ?? movements.reduce((sum, m) => sum + (m.haber || 0), 0);
    const saldoActual = selectedClient?.current_balance ?? (movements.length > 0 ? movements[movements.length - 1].saldo : 0);
    // ✅ Use item count from API summary (8 items), not movement count (2 movements)
    const productosNoFacturados = selectedClient?.productosSinFacturar ?? movements.filter(m => m.tipo === 'PRODUCTO' && !m.factura_id).length;
    // ✅ Updated price fields (current catalog prices)
    const updatedTotalDebe = selectedClient?.updatedTotalDebe ?? null;
    const updatedSaldoActual = selectedClient?.updatedSaldoActual ?? null;
    const saldoActualDeviation = selectedClient?.saldoActualDeviation ?? null;

    // Counts for action badges — computed from current selection
    // invoiceableCount: selected CARGO rows that have at least one invoiceable item
    const invoiceableCount = movements.filter(m =>
        selectedRowKeys.includes(m.id) &&
        (m.tipo === 'PRODUCTO' || m.tipo === 'PRODUCTO_FACTURADO') &&
        m.items?.some(item => item.itemStatus === 'PENDING' || item.canBeReinvoiced)
    ).length;
    // archivableCount: selected rows that pass the canArchive business rule
    const archivableCount = movements.filter(m =>
        selectedRowKeys.includes(m.id) && canArchive(m)
    ).length;
    // settleableSelectedCount: selected CARGO rows eligible for liquidación
    const settleableSelectedCount = movements.filter(m =>
        selectedRowKeys.includes(m.id) && canSettle(m)
    ).length;
    // availableCredit: crédito disponible del cliente (when saldo < 0 the client has credit)
    const saldoActualValue = selectedClient?.saldo_pendiente ?? selectedClient?.current_balance ?? 0;
    const availableCredit = saldoActualValue < 0 ? Math.abs(saldoActualValue) : 0;

    // Handle Apply Filters
    const handleApplyFilters = () => {
        if (selectedClient) {
            fetchMovements(selectedClient.id || selectedClient.documento_nro, 1, pagination.pageSize);
        }
    };

    // Handle Table pagination/sort changes
    const handleTableChange = (newPagination) => {
        if (selectedClient) {
            fetchMovements(
                selectedClient.id || selectedClient.documento_nro,
                newPagination.current,
                newPagination.pageSize
            );
        }
    };

    // Handle Clear Filters
    const handleClearFilters = () => {
        setFilters({ dateFrom: null, dateTo: null });
    };

    // ========== VOID MOVEMENT FUNCTIONS ==========
    
    /**
     * Check if a movement can be voided based on business rules
     * @param {Object} movement - The movement record
     * @returns {{ canVoid: boolean, reason: string }}
     */
    const canVoidMovement = (movement) => {
        // Already voided
        if (movement.status === 'VOIDED') {
            return { canVoid: false, reason: 'Este movimiento ya fue anulado' };
        }
        
        // Cannot void reversal movements (ANULACION)
        if (movement.tipoOriginal === 'ANULACION' || movement.tipoOrigen === 'VOID') {
            return { canVoid: false, reason: 'No se puede anular un movimiento de anulación' };
        }
        
        // For CARGO movements, check if items have active invoices
        if (movement.tipoOriginal === 'CARGO' && movement.items && movement.items.length > 0) {
            const hasActiveInvoicedItems = movement.items.some(
                item => item.itemStatus === 'INVOICED' && !item.canBeReinvoiced
            );
            if (hasActiveInvoicedItems) {
                return { 
                    canVoid: false, 
                    reason: 'Tiene facturas activas. Primero debe crear una Nota de Crédito.' 
                };
            }
        }
        
        return { canVoid: true, reason: '' };
    };

    /**
     * Open void confirmation modal
     */
    const handleOpenVoidModal = (movement) => {
        const { canVoid, reason } = canVoidMovement(movement);
        
        if (!canVoid) {
            message.warning(reason);
            return;
        }
        
        setMovementToVoid(movement);
        setVoidReason('');
        setVoidModalVisible(true);
    };

    /**
     * Sync movement DEBE to current catalog price (updatedDebe)
     */
    const handleSyncDebe = async () => {
        if (!movementToSync) return;
        setSyncLoading(true);
        try {
            const result = await runningAccountService.syncMovementDebe(movementToSync.id);
            if (result.success && result.changed) {
                message.success(
                    `DEBE sincronizado: $ ${result.oldDebit?.toLocaleString('es-AR', { minimumFractionDigits: 2 })} → $ ${result.newDebit?.toLocaleString('es-AR', { minimumFractionDigits: 2 })} · ${result.cascadeCount} movimiento(s) recalculado(s)`,
                    6
                );
                setSyncConfirmVisible(false);
                setMovementToSync(null);
                if (selectedClient) {
                    await fetchMovements(selectedClient.id || selectedClient.documento_nro);
                }
            } else if (result.success && result.changed === false) {
                message.info('El DEBE ya estaba actualizado — no se realizaron cambios.');
                setSyncConfirmVisible(false);
                setMovementToSync(null);
            } else {
                message.error(result.message || 'Error al sincronizar el DEBE');
            }
        } catch (err) {
            message.error('Error al sincronizar: ' + err.message);
        } finally {
            setSyncLoading(false);
        }
    };

    /**
     * Execute void movement
     */
    const handleVoidMovement = async () => {
        if (!movementToVoid) return;
        
        if (!voidReason || voidReason.trim().length < 5) {
            message.error('El motivo de anulación debe tener al menos 5 caracteres');
            return;
        }
        
        setVoidLoading(true);
        try {
            const result = await runningAccountService.voidMovement(
                movementToVoid.id, 
                voidReason.trim()
            );
            
            if (result.success) {
                message.success('Movimiento anulado exitosamente');
                setVoidModalVisible(false);
                setMovementToVoid(null);
                setVoidReason('');
                
                // Refresh movements
                if (selectedClient) {
                    fetchMovements(selectedClient.id || selectedClient.documento_nro);
                }
                // Refresh clients to update balance
                fetchClients();
            } else {
                // Handle specific errors
                const errorMessages = {
                    'MOVEMENT_NOT_FOUND': 'Movimiento no encontrado',
                    'ALREADY_VOIDED': 'Este movimiento ya fue anulado',
                    'INVALID_OPERATION': 'No se puede anular un movimiento de anulación',
                    'HAS_ACTIVE_INVOICES': 'No se puede anular. Primero debe crear una Nota de Crédito para la factura asociada.',
                    'INVALID_REASON': 'El motivo de anulación debe tener al menos 5 caracteres'
                };
                
                const errorMsg = errorMessages[result.error] || result.message || 'Error al anular movimiento';
                message.error(errorMsg);
            }
        } catch (error) {
            console.error('[CuentasAbiertas] Error voiding movement:', error);
            message.error('Error al anular el movimiento');
        } finally {
            setVoidLoading(false);
        }
    };

    // Handle pickup status update
    const handleUpdatePickup = async () => {
        if (!movementToPickup) return;
        
        setPickupLoading(true);
        try {
            const newPickupStatus = !movementToPickup.pickedUp;
            
            console.log('[CuentasAbiertas] Updating pickup for movement:', {
                movementId: movementToPickup.id,
                currentStatus: movementToPickup.pickedUp,
                newStatus: newPickupStatus,
                pickedUpBy: pickupData.pickedUpBy,
                affectsStock: pickupData.affectsStock
            });
            
            const result = await runningAccountService.updatePickupStatus(
                movementToPickup.id,
                {
                    pickedUp: newPickupStatus,
                    pickedUpBy: newPickupStatus ? pickupData.pickedUpBy.trim() || null : null,
                    affectsStock: pickupData.affectsStock
                }
            );
            
            console.log('[CuentasAbiertas] Pickup update response:', result);
            
            message.success(`Estado de retiro actualizado: ${newPickupStatus ? 'Retirado' : 'Pendiente'}`);
            setPickupModalVisible(false);
            setMovementToPickup(null);
            setPickupData({ pickedUpBy: '', affectsStock: true });
            
            // Refresh movements
            console.log('[CuentasAbiertas] Refreshing movements after pickup update...');
            if (selectedClient) {
                await fetchMovements(selectedClient.id || selectedClient.documento_nro);
            }
            console.log('[CuentasAbiertas] Movements refreshed');
            
        } catch (error) {
            console.error('[CuentasAbiertas] Error updating pickup:', error);
            message.error('Error al actualizar estado de retiro');
        } finally {
            setPickupLoading(false);
        }
    };

    return (
        <div>
            {/* Action Buttons Row */}
            {!isDemoMode && <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, gap: 8, flexWrap: 'wrap', overflow: 'visible', paddingTop: 14 }}>
                {/* Client Selector */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', minWidth: 0 }}>
                    <Select
                        placeholder="Seleccionar Cliente"
                        style={{ width: 260, minWidth: 180, maxWidth: '100%' }}
                        loading={loadingClients}
                        allowClear
                        showSearch
                        value={selectedClient?.id || selectedClient?.documento_nro || undefined}
                        onChange={handleClientChange}
                        filterOption={(input, option) => {
                            const q = input.toLowerCase();
                            const c = clients.find(cl => (cl.id || cl.documento_nro) === option.value);
                            if (!c) return false;
                            const name  = (c.business_name || c.razon_social || c.nombre || '').toLowerCase();
                            const docNr = (c.document_number || c.documento_nro || '').toLowerCase();
                            const alias = (c.alias || '').toLowerCase();
                            return name.includes(q) || docNr.includes(q) || alias.includes(q);
                        }}
                    >
                        {clients.map((client) => {
                            const balance = client.current_balance || client.saldo_pendiente || 0;
                            return (
                                <Option
                                    key={client.id || client.documento_nro}
                                    value={client.id || client.documento_nro}
                                >
                                    {client.business_name || client.razon_social || client.nombre}
                                    {client.alias ? ` (${client.alias})` : ''}
                                    {' — '}{client.document_number || client.documento_nro || 'S/D'}
                                    {balance > 0 && ` · $${balance.toLocaleString('es-AR', { minimumFractionDigits: 2 })}`}
                                </Option>
                            );
                        })}
                    </Select>

                    {/* Editar Cliente button — always rendered, disabled when no client selected */}
                    <Button
                        icon={<EditOutlined />}
                        disabled={!selectedClient}
                        onClick={() => selectedClient && setEditarClienteModalVisible(true)}
                        title="Editar datos del cliente"
                        style={{
                            backgroundColor: selectedClient ? '#fffbe6' : undefined,
                            borderColor: selectedClient ? '#ffe58f' : undefined,
                            color: selectedClient ? '#ad6800' : undefined,
                            flexShrink: 0
                        }}
                    >
                        Editar Cliente
                    </Button>
                </div>

                {/* Action Buttons */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', overflow: 'visible', paddingTop: 4 }}>
                    <Button
                        size="large"
                        icon={<UserAddOutlined />}
                        onClick={() => setAgregarClienteModalVisible(true)}
                        style={{ 
                            backgroundColor: '#722ed1', 
                            borderColor: '#722ed1', 
                            color: 'white' 
                        }}
                    >
                        Agregar Cliente
                    </Button>
                    <Button
                        size="large"
                        icon={<PlusOutlined />}
                        disabled={!selectedClient}
                        onClick={() => setAgregarMovimientoModalVisible(true)}
                        style={{ 
                            backgroundColor: selectedClient ? '#13c2c2' : undefined, 
                            borderColor: selectedClient ? '#13c2c2' : undefined, 
                            color: selectedClient ? 'white' : undefined 
                        }}
                    >
                        Agregar Movimiento
                    </Button>
                    <div style={{ display: 'inline-block', position: 'relative', paddingTop: 10, marginTop: -10 }}>
                    <Badge count={invoiceableCount} style={{ backgroundColor: '#096dd9' }}>
                    <Button
                        type="primary"
                        size="large"
                        icon={<FileTextOutlined />}
                        disabled={invoiceableCount === 0}
                        onClick={() => {
                            // Build items from selected CARGO movements with items array
                            const itemsToInvoice = [];
                            
                            movements
                                // Include PRODUCTO (pending) and PRODUCTO_FACTURADO (for re-invoicing cancelled invoices)
                                .filter(m => selectedRowKeys.includes(m.id) && (m.tipo === 'PRODUCTO' || m.tipo === 'PRODUCTO_FACTURADO'))
                                .forEach(m => {
                                    // If movement has items array, extract each item
                                    if (m.items && m.items.length > 0) {
                                        // Calculate discount proportion for multi-item movements
                                        const movementSubtotal = m.items.reduce((sum, item) => sum + (item.subtotalAtLoad || 0), 0);
                                        const movementDiscountPct = m.descuentoPorcentaje || 0;
                                        
                                        console.log('[CuentasAbiertas] Movement discount info:', {
                                            movementId: m.id,
                                            descuentoPorcentaje: m.descuentoPorcentaje,
                                            descuentoMonto: m.descuentoMonto,
                                            debe: m.debe,
                                            movementSubtotal
                                        });
                                        
                                        m.items.forEach(item => {
                                            // Include PENDING items OR items that can be re-invoiced (cancelled invoice)
                                            if (item.itemStatus === 'PENDING' || item.canBeReinvoiced) {
                                                // Calculate this item's share of the discount
                                                const itemSubtotal = item.subtotalAtLoad || 0;
                                                const itemDiscountAmount = itemSubtotal * (movementDiscountPct / 100);
                                                const itemFinalAmount = itemSubtotal - itemDiscountAmount;
                                                
                                                itemsToInvoice.push({
                                                    itemId: item.id,
                                                    movementId: m.id,
                                                    productId: item.productId,
                                                    productCode: item.productCode,
                                                    productName: item.productName,
                                                    cantidad: item.quantity,
                                                    splitter: item.splitter || 1,
                                                    precioCarga: item.unitPriceAtLoad,
                                                    subtotalCarga: item.subtotalAtLoad, // Pre-discount subtotal
                                                    // ✅ Include discount info from movement
                                                    descuentoPorcentaje: movementDiscountPct,
                                                    descuentoMonto: Number(itemDiscountAmount.toFixed(2)),
                                                    subtotalFinal: Number(itemFinalAmount.toFixed(2)), // Post-discount amount (what should be invoiced)
                                                    // currentPrice is pack-level (RetailPrice from dbo.Prices), divide by splitter to get unit price
                                                    // This keeps precioActual comparable to precioCarga (both unit-level)
                                                    precioActual: item.currentPrice != null
                                                        ? item.currentPrice / (item.splitter || 1)
                                                        : item.unitPriceAtLoad,
                                                    priceChanged: item.priceChanged ?? false, // Pre-computed by backend (pack-level comparison, no false positives)
                                                    isReinvoice: item.canBeReinvoiced || false,
                                                    cancelledInvoiceNumber: item.cancelledInvoiceNumber || null,
                                                    // If the backend wrote a sync note, the DEBE was updated but items keep old unitPriceAtLoad.
                                                    // Signal the invoice modal to default to 'actual' price for those items.
                                                    wasMovementSynced: !!(m.notasInternas?.includes('DEBE ACTUALIZADO')),
                                                    isPaid: m.isPaid ?? false
                                                });
                                            }
                                        });
                                    } else {
                                        // Fallback for movements without items array
                                        console.warn('[CuentasAbiertas] Movement without items array:', m.id);
                                        itemsToInvoice.push({
                                            itemId: m.id,
                                            movementId: m.id,
                                            productId: null,
                                            productCode: m.descripcion?.split(' - ')[0] || 'PROD',
                                            productName: m.descripcion || 'Producto',
                                            cantidad: m.cantidad || 1,
                                            precioCarga: m.precio_unitario || m.valor || 0,
                                            precioActual: m.precio_unitario || m.valor || 0,
                                            descuentoPorcentaje: 0,
                                            descuentoMonto: 0,
                                            subtotalFinal: m.precio_unitario || m.valor || 0,
                                            isPaid: m.isPaid ?? false
                                        });
                                    }
                                });
                            
                            if (itemsToInvoice.length === 0) {
                                message.warning('Seleccione al menos un producto (no movimientos) para facturar');
                                return;
                            }
                            
                            console.log('[CuentasAbiertas] Items to invoice:', itemsToInvoice);
                            setSelectedItemsForInvoice(itemsToInvoice);
                            setFacturarModalVisible(true);
                        }}
                        style={{ 
                            backgroundColor: invoiceableCount > 0 ? '#52c41a' : undefined, 
                            borderColor: invoiceableCount > 0 ? '#52c41a' : undefined
                        }}
                    >
                        Facturar
                    </Button>
                    </Badge>
                    </div>
                    <div style={{ display: 'inline-block', position: 'relative', paddingTop: 10, marginTop: -10 }}>
                    <Badge count={settleableSelectedCount} style={{ backgroundColor: '#096dd9' }}>
                    <Button
                        size="large"
                        icon={<HighlightOutlined />}
                        disabled={settleableSelectedCount === 0 || !selectedClient}
                        loading={settlementLoading}
                        onClick={() => openLiquidarModal(selectedRowKeys)}
                        style={{
                            backgroundColor: settleableSelectedCount > 0 ? '#cf1322' : undefined,
                            borderColor:     settleableSelectedCount > 0 ? '#cf1322' : undefined,
                            color:           settleableSelectedCount > 0 ? 'white'   : undefined
                        }}
                    >
                        Liquidar
                    </Button>
                    </Badge>
                    </div>
                    <div style={{ display: 'inline-block', position: 'relative', paddingTop: 10, marginTop: -10 }}>
                    <Badge count={archivableCount} style={{ backgroundColor: '#096dd9' }}>
                    <Button
                        size="large"
                        icon={<InboxOutlined />}
                        disabled={archivableCount === 0 || !selectedClient}
                        loading={archivingLoading}
                        onClick={handleArchiveSelected}
                        style={{
                            backgroundColor: archivableCount > 0 ? '#595959' : undefined,
                            borderColor:     archivableCount > 0 ? '#595959' : undefined,
                            color:           archivableCount > 0 ? 'white'   : undefined
                        }}
                    >
                        Archivar
                    </Button>
                    </Badge>
                    </div>
                    <Button
                        size="large"
                        icon={<ReloadOutlined />}
                        title="Actualizar"
                        onClick={() => {
                            fetchClients();
                            if (selectedClient) {
                                fetchMovements(selectedClient.id || selectedClient.documento_nro);
                            } else {
                                fetchAggregateStatistics();
                            }
                        }}
                        style={{ 
                            backgroundColor: '#95de64', 
                            borderColor: '#95de64', 
                            color: 'white'
                        }}
                    />
                </div>
            </div>}

            {/* Summary Cards - Conditional rendering based on client selection */}
            <div style={{ marginBottom: 24, display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                {selectedClient ? (
                    // Individual Client Statistics
                    <>
                        <Card size="small" style={{ borderLeft: '4px solid #003a8c', flex: '1', minWidth: '200px' }}>
                            {loading ? (
                                <div style={{ textAlign: 'center', padding: '40px 0' }}><Spin size="large" /></div>
                            ) : (
                                <>
                                    <Statistic
                                        title={<span style={{ fontSize: '16px' }}>CLIENTE SELECCIONADO</span>}
                                        value={selectedClient.alias || selectedClient.display_name || selectedClient.business_name || selectedClient.razon_social || selectedClient.nombre || 'Cliente sin nombre'}
                                        valueStyle={{ fontSize: '22px', fontWeight: 'bold', color: '#003a8c' }}
                                    />
                                    {(() => {
                                        const top = selectedClient.alias || selectedClient.display_name;
                                        const sub = selectedClient.business_name || selectedClient.razon_social;
                                        if (top && sub && sub !== top) {
                                            return (
                                                <div style={{ fontSize: '12px', color: '#8c8c8c', marginTop: 2, lineHeight: 1.4 }}>
                                                    {sub}
                                                </div>
                                            );
                                        }
                                        return null;
                                    })()}
                                </>
                            )}
                        </Card>
                        <Card size="small" style={{ borderLeft: '4px solid #faad14', flex: '1', minWidth: '200px' }}>
                            {loading ? (
                                <div style={{ textAlign: 'center', padding: '40px 0' }}><Spin size="large" /></div>
                            ) : (
                                <Statistic
                                    title={
                                        <Tooltip title="Cantidad de productos cargados que no han sido facturados">
                                            <span style={{ fontSize: '16px' }}>ITEMS SIN FACTURAR</span>
                                        </Tooltip>
                                    }
                                    value={productosNoFacturados}
                                    valueStyle={{ color: productosNoFacturados > 0 ? '#faad14' : '#52c41a', fontSize: '24px' }}
                                    suffix={<ShoppingCartOutlined />}
                                />
                            )}
                        </Card>
                        <Card size="small" style={{ borderLeft: '4px solid #cf1322', flex: '1', minWidth: '200px' }}>
                            {loading ? (
                                <div style={{ textAlign: 'center', padding: '40px 0' }}><Spin size="large" /></div>
                            ) : (
                                <>
                                    <Statistic
                                        title={<span style={{ fontSize: '16px' }}>TOTAL DEBE</span>}
                                        value={totalDebe}
                                        precision={0}
                                        prefix="$"
                                        valueStyle={{ color: '#cf1322', fontSize: '24px' }}
                                    />
                                    {updatedTotalDebe != null && Math.abs(updatedTotalDebe - totalDebe) > 0.01 && (
                                        <div style={{ marginTop: 4, fontSize: 12, color: updatedTotalDebe > totalDebe ? '#fa8c16' : '#52c41a' }}>
                                            {updatedTotalDebe > totalDebe ? <RiseOutlined style={{ marginRight: 3 }} /> : <FallOutlined style={{ marginRight: 3 }} />}
                                            <span style={{ color: '#8c8c8c' }}>Actualizado: </span>
                                            $ {Math.round(updatedTotalDebe).toLocaleString('es-AR')}
                                        </div>
                                    )}
                                </>
                            )}
                        </Card>
                        <Card size="small" style={{ borderLeft: '4px solid #52c41a', flex: '1', minWidth: '200px' }}>
                            {loading ? (
                                <div style={{ textAlign: 'center', padding: '40px 0' }}><Spin size="large" /></div>
                            ) : (
                                <Statistic
                                    title={<span style={{ fontSize: '16px' }}>TOTAL HABER</span>}
                                    value={Math.abs(totalHaber)}
                                    precision={2}
                                    prefix="$"
                                    valueStyle={{ color: '#52c41a', fontSize: '24px' }}
                                />
                            )}
                        </Card>
                        <Card size="small" style={{ borderLeft: '4px solid #722ed1', flex: '1', minWidth: '200px' }}>
                            {loading ? (
                                <div style={{ textAlign: 'center', padding: '40px 0' }}><Spin size="large" /></div>
                            ) : (
                                <>
                                    <Statistic
                                        title={
                                            <Space>
                                                <span style={{ fontSize: '16px' }}>SALDO</span>
                                                <Tooltip title="Ver detalle del saldo">
                                                    <InfoCircleOutlined
                                                        style={{ cursor: 'pointer', color: '#722ed1' }}
                                                        onClick={(e) => { e.stopPropagation(); setSaldoInfoVisible(true); }}
                                                    />
                                                </Tooltip>
                                            </Space>
                                        }
                                        value={Math.abs(saldoActual)}
                                        precision={0}
                                        prefix={
                                            <span style={{ color: saldoActual > 0 ? '#cf1322' : saldoActual < 0 ? '#237804' : '#8c8c8c', fontWeight: 'bold' }}>
                                                {saldoActual > 0 ? '−' : saldoActual < 0 ? '+' : ''}&thinsp;$&thinsp;
                                            </span>
                                        }
                                        valueStyle={{ color: saldoActual > 0 ? '#cf1322' : saldoActual < 0 ? '#237804' : '#8c8c8c', fontWeight: 'bold', fontSize: '24px' }}
                                        suffix={<WalletOutlined style={{ color: saldoActual > 0 ? '#cf1322' : '#237804' }} />}
                                    />
                                    {updatedSaldoActual != null && Math.abs(updatedSaldoActual - saldoActual) > 0.01 && (
                                        <div style={{ marginTop: 4, fontSize: 12, color: saldoActualDeviation > 0 ? '#fa8c16' : '#52c41a' }}>
                                            {saldoActualDeviation > 0 ? <RiseOutlined style={{ marginRight: 3 }} /> : <FallOutlined style={{ marginRight: 3 }} />}
                                            <span style={{ color: '#8c8c8c' }}>Actualizado: </span>
                                            $ {Math.round(Math.abs(updatedSaldoActual)).toLocaleString('es-AR')}
                                            {saldoActualDeviation != null && (
                                                <span style={{ marginLeft: 4 }}>
                                                    ({saldoActualDeviation > 0 ? '+' : ''}{saldoActualDeviation.toFixed(2)}%)
                                                </span>
                                            )}
                                        </div>
                                    )}
                                </>
                            )}
                        </Card>
                        <Card 
                            size="small" 
                            hoverable
                            style={{ 
                                borderLeft: '4px solid #1890ff',
                                flex: '1',
                                minWidth: '200px',
                                cursor: 'pointer',
                                transition: 'all 0.3s'
                            }}
                            onClick={() => {
                                const clientId = selectedClient.id || selectedClient.documento_nro;
                                window.open(`#/print-ra?clientId=${clientId}`, '_blank');
                            }}
                        >
                            <div style={{ textAlign: 'center' }}>
                                <PrinterOutlined style={{ fontSize: 24, color: '#1890ff', marginBottom: 8 }} />
                                <div style={{ fontSize: 16, fontWeight: 'bold', color: '#1890ff' }}>
                                    Imprimir Ficha
                                </div>
                            </div>
                        </Card>
                    </>
                ) : (
                    // Aggregate Statistics (when no client selected)
                    <>
                        <Card size="small" loading={loadingStats} style={{ borderLeft: '4px solid #1890ff', flex: '1', minWidth: '200px' }}>
                            <Statistic
                                title={
                                    <Tooltip title="Clientes con estado ACTIVO en el sistema de cuentas abiertas">
                                        <span style={{ fontSize: '16px' }}>CLIENTES ACTIVOS</span>
                                    </Tooltip>
                                }
                                value={aggregateStats?.clients?.active ?? aggregateStats?.activeClients ?? aggregateStats?.clientes?.activos ?? 0}
                                valueStyle={{ color: '#1890ff', fontSize: '24px' }}
                                suffix={<UserAddOutlined />}
                            />
                        </Card>
                        <Card 
                            size="small" 
                            loading={loadingStats}
                            style={{ borderLeft: '4px solid #cf1322', flex: '1', minWidth: '200px' }}
                        >
                            <Statistic
                                title={
                                    <Space>
                                        <span style={{ fontSize: '16px' }}>SALDO TOTAL ADEUDADO</span>
                                        <Tooltip title="Ver detalle del cálculo">
                                            <InfoCircleOutlined 
                                                style={{ cursor: 'pointer', color: '#1890ff' }}
                                                onClick={() => setBalanceBreakdownVisible(true)}
                                            />
                                        </Tooltip>
                                    </Space>
                                }
                                value={Math.ceil(aggregateStats?.balance?.total ?? aggregateStats?.totalPendingBalance ?? aggregateStats?.saldos?.totalPendiente ?? 0)}
                                prefix="$"
                                valueStyle={{ color: '#cf1322', fontWeight: 'bold', fontSize: '24px' }}
                                suffix={<WalletOutlined />}
                            />
                            {(() => {
                                const aggTotal = aggregateStats?.balance?.total ?? aggregateStats?.totalPendingBalance ?? 0;
                                const aggUpdated = aggregateStats?.balance?.updatedTotal ?? null;
                                if (aggUpdated == null || Math.abs(aggUpdated - aggTotal) < 0.01) return null;
                                const aggDev = aggTotal > 0.01 ? ((aggUpdated - aggTotal) / Math.abs(aggTotal) * 100) : null;
                                const isUp = aggUpdated > aggTotal;
                                return (
                                    <div style={{ marginTop: 4, fontSize: 12, color: isUp ? '#fa8c16' : '#52c41a' }}>
                                        {isUp ? <RiseOutlined style={{ marginRight: 3 }} /> : <FallOutlined style={{ marginRight: 3 }} />}
                                        <span style={{ color: '#8c8c8c' }}>Precios actuales: </span>
                                        $ {Math.round(aggUpdated).toLocaleString('es-AR')}
                                        {aggDev != null && (
                                            <span style={{ marginLeft: 4 }}>({isUp ? '+' : ''}{aggDev.toFixed(1)}%)</span>
                                        )}
                                    </div>
                                );
                            })()}
                        </Card>
                        <Card size="small" loading={loadingStats} style={{ borderLeft: '4px solid #faad14', flex: '1', minWidth: '200px' }}>
                            <Statistic
                                title={
                                    <Tooltip title="Cantidad de productos cargados que aún no han sido facturados">
                                        <span style={{ fontSize: '16px' }}>ITEMS PENDIENTES</span>
                                    </Tooltip>
                                }
                                value={aggregateStats?.invoicing?.pendingItems ?? aggregateStats?.pendingItems ?? aggregateStats?.facturacion?.itemsSinFacturar ?? 0}
                                valueStyle={{ 
                                    color: (aggregateStats?.invoicing?.pendingItems ?? aggregateStats?.pendingItems ?? aggregateStats?.facturacion?.itemsSinFacturar ?? 0) > 0 
                                        ? '#faad14' 
                                        : '#52c41a',
                                    fontSize: '24px'
                                }}
                                suffix={<ShoppingCartOutlined />}
                            />
                        </Card>
                        <Card size="small" loading={loadingStats} style={{ borderLeft: '4px solid #52c41a', flex: '1', minWidth: '200px' }}>
                            <Statistic
                                title={
                                    <Tooltip title="Cantidad de cargos, pagos o ajustes registrados hoy">
                                        <span style={{ fontSize: '16px' }}>MOVIMIENTOS HOY</span>
                                    </Tooltip>
                                }
                                value={aggregateStats?.movements?.today ?? aggregateStats?.movementsToday ?? aggregateStats?.movimientos?.movimientosHoy ?? 0}
                                valueStyle={{ color: '#52c41a', fontSize: '24px' }}
                                suffix={<SwapOutlined />}
                            />
                        </Card>
                    </>
                )}
            </div>

            {/* Filters */}
            {!isDemoMode && <div style={{ marginBottom: 16, background: 'white', padding: 16, borderRadius: 8, border: '1px solid #f0f0f0' }}>
                <Space wrap>
                    <RangePicker
                        format="DD/MM/YYYY"
                        placeholder={['Desde', 'Hasta']}
                        value={[
                            filters.dateFrom ? dayjs(filters.dateFrom) : null,
                            filters.dateTo ? dayjs(filters.dateTo) : null
                        ]}
                        onChange={(dates) => {
                            setFilters({
                                ...filters,
                                dateFrom: dates ? dates[0].format('YYYY-MM-DD') : null,
                                dateTo: dates ? dates[1].format('YYYY-MM-DD') : null,
                            });
                        }}
                    />
                    <Button type="primary" onClick={handleApplyFilters} disabled={!selectedClient}>
                        Aplicar Filtros
                    </Button>
                    <Button onClick={handleClearFilters}>
                        Limpiar
                    </Button>
                    <Switch
                        checked={showArchivedMovements}
                        onChange={(checked) => setShowArchivedMovements(checked)}
                        checkedChildren="Archivados: ON"
                        unCheckedChildren="Archivados: OFF"
                        disabled={!selectedClient}
                        style={{ marginLeft: 8 }}
                    />
                    <Switch
                        checked={showVoidedMovements}
                        onChange={(checked) => setShowVoidedMovements(checked)}
                        checkedChildren="Anulados: ON"
                        unCheckedChildren="Anulados: OFF"
                        disabled={!selectedClient}
                    />
                </Space>
            </div>}

            {/* Table */}
            {selectedClient ? (
                <Table
                    rowSelection={rowSelection}
                    columns={columns}
                    dataSource={movements.filter(m => !(m.tipo === 'LIQUIDACION' && m.movimientoOriginalId != null))}
                    rowKey="id"
                    loading={loading}
                    pagination={{
                        ...pagination,
                        showSizeChanger: true,
                        showTotal: (total) => `Total: ${total} registros`,
                        locale: { items_per_page: '/ pág.' },
                    }}
                    onChange={handleTableChange}
                    scroll={{ x: 1000 }}
                    size="middle"
                    bordered
                    rowClassName={(record) => record.isArchived ? 'ra-row-archived' : ''}
                    expandable={{
                        // Only expand CARGO rows that have a linked LIQUIDACION
                        rowExpandable: (record) =>
                            (record.tipo === 'PRODUCTO' || record.tipo === 'PRODUCTO_FACTURADO') &&
                            record.isPaid === true &&
                            movements.some(m => m.tipo === 'LIQUIDACION' && m.movimientoOriginalId === record.id),
                        expandedRowKeys,
                        onExpandedRowsChange: setExpandedRowKeys,
                        expandedRowRender: (record) => {
                            const liquidacion = movements.find(
                                m => m.tipo === 'LIQUIDACION' && m.movimientoOriginalId === record.id
                            );
                            if (!liquidacion) return null;
                            return (
                                <div style={{
                                    background: '#f9f0ff',
                                    border: '1px solid #d3adf7',
                                    borderRadius: 6,
                                    padding: '10px 16px',
                                    display: 'flex',
                                    gap: 16,
                                    alignItems: 'center',
                                    flexWrap: 'wrap',
                                }}>
                                    <Tag color="purple" icon={<BankOutlined />} style={{ fontSize: 13 }}>
                                        Liquidación aplicada
                                    </Tag>
                                    <span style={{ color: '#595959', fontSize: 12 }}>
                                        <strong>Fecha:</strong> {liquidacion.fecha
                                            ? String(liquidacion.fecha).split('T')[0].split('-').reverse().join('/')
                                            : '-'}
                                    </span>
                                    <span style={{ color: '#595959', fontSize: 12 }}>
                                        <strong>Descripción:</strong> {liquidacion.descripcion}
                                    </span>
                                    {liquidacion.transactionId && (
                                        <span style={{ color: '#8c8c8c', fontSize: 11 }}>
                                            Ref: {liquidacion.transactionId}
                                        </span>
                                    )}
                                    <span style={{ color: '#52c41a', fontWeight: 600, fontSize: 13, marginLeft: 'auto' }}>
                                        HABER: {formatPrice(Math.abs(liquidacion.haber))}
                                    </span>
                                    <span style={{
                                        color: liquidacion.saldo > 0 ? '#cf1322' : '#52c41a',
                                        fontWeight: 600,
                                        fontSize: 13
                                    }}>
                                        SALDO: {formatPrice(Math.abs(liquidacion.saldo))}
                                    </span>
                                </div>
                            );
                        },
                    }}
                    summary={() => (
                        <Table.Summary fixed>
                            <Table.Summary.Row style={{ backgroundColor: '#fafafa' }}>
                                <Table.Summary.Cell index={0} colSpan={6}>
                                    <Text strong>TOTALES</Text>
                                </Table.Summary.Cell>
                                <Table.Summary.Cell index={1} align="right">
                                    <Text strong style={{ color: '#cf1322' }}>{formatPrice(Math.abs(totalDebe))}</Text>
                                </Table.Summary.Cell>
                                <Table.Summary.Cell index={2} align="right">
                                    <Text strong style={{ color: '#52c41a' }}>{formatPrice(Math.abs(totalHaber))}</Text>
                                </Table.Summary.Cell>
                                <Table.Summary.Cell index={3} align="right">
                                    <Text strong style={{ color: saldoActual > 0 ? '#cf1322' : '#52c41a' }}>
                                        {formatPrice(Math.abs(saldoActual))}
                                    </Text>
                                </Table.Summary.Cell>
                            </Table.Summary.Row>
                        </Table.Summary>
                    )}
                />
            ) : (
                <div style={{ 
                    background: 'white', 
                    padding: 24, 
                    borderRadius: 8, 
                    border: '1px solid #f0f0f0'
                }}>
                    {/* Top Clients Summary Table when no client selected */}
                    {aggregateStats?.topClients && aggregateStats.topClients.length > 0 ? (
                        <div>
                            <Text strong style={{ fontSize: 16, display: 'block', marginBottom: 16 }}>
                                📊 Top 5 Clientes por Saldo Pendiente
                            </Text>
                            <Table
                                dataSource={aggregateStats.topClients}
                                rowKey="clientId"
                                size="small"
                                pagination={false}
                                loading={loadingStats}
                                columns={[
                                    {
                                        title: 'Cliente',
                                        dataIndex: 'businessName',
                                        key: 'businessName',
                                        render: (text, record) => <Text strong>{text || record.displayName || record.nombre}</Text>
                                    },
                                    {
                                        title: 'Saldo Pendiente',
                                        dataIndex: 'currentBalance',
                                        key: 'currentBalance',
                                        align: 'right',
                                        render: (value, record) => (
                                            <Text style={{ color: '#cf1322', fontWeight: 'bold' }}>
                                                {formatPrice(value || record.saldoPendiente || 0)}
                                            </Text>
                                        )
                                    },
                                    {
                                        title: 'Último Movimiento',
                                        dataIndex: 'lastMovement',
                                        key: 'lastMovement',
                                        render: (value, record) => formatDate(value || record.ultimoMovimiento)
                                    },
                                    ...(!isDemoMode ? [{
                                        title: 'Acción',
                                        key: 'action',
                                        render: (_, record) => (
                                            <Button 
                                                type="link" 
                                                size="small"
                                                onClick={() => {
                                                    // Find and select this client
                                                    const client = clients.find(c => c.id === record.clientId);
                                                    if (client) {
                                                        handleClientChange(client.id || client.documento_nro);
                                                    }
                                                }}
                                            >
                                                Ver Detalle
                                            </Button>
                                        )
                                    }] : [])
                                ]}
                            />
                            <div style={{ marginTop: 16, textAlign: 'center' }}>
                                <Text type="secondary">
                                    Seleccione un cliente del selector superior para ver todos sus movimientos
                                </Text>
                            </div>
                        </div>
                    ) : (
                        <Empty
                            image={Empty.PRESENTED_IMAGE_SIMPLE}
                            description={
                                <span>
                                    Seleccione un cliente para ver sus movimientos de cuenta abierta
                                </span>
                            }
                        />
                    )}
                </div>
            )}

            {/* Agregar Cliente Modal */}
            <AgregarClienteRunningAccountModal
                visible={agregarClienteModalVisible}
                onClose={() => setAgregarClienteModalVisible(false)}
                onClientCreated={(newClient) => {
                    console.log('[CuentasAbiertas] New client created:', newClient);
                    fetchClients(); // Refresh client list
                    message.success(`Cliente "${newClient.nombre || newClient.razon_social}" agregado exitosamente`);
                }}
            />

            {/* Editar Cliente Modal */}
            <EditarClienteRunningAccountModal
                visible={editarClienteModalVisible}
                onClose={() => setEditarClienteModalVisible(false)}
                client={selectedClient}
                onClientUpdated={(updatedClient) => {
                    console.log('[CuentasAbiertas] Client updated:', updatedClient);
                    // Update the client in the list without re-fetching
                    setClients(prev =>
                        prev.map(c =>
                            c.id === updatedClient.id ? { ...c, ...updatedClient } : c
                        )
                    );
                    // Also update the selected client reference
                    setSelectedClient(prev => prev ? { ...prev, ...updatedClient } : prev);
                    setEditarClienteModalVisible(false);
                }}
            />

            {/* Agregar Movimiento Modal */}
            <AgregarMovimientoModal
                visible={agregarMovimientoModalVisible}
                onClose={() => setAgregarMovimientoModalVisible(false)}
                selectedClient={selectedClient}
                onMovementCreated={(newMovement) => {
                    console.log('[CuentasAbiertas] New movement created:', newMovement);
                    // Refresh movements for current client
                    if (selectedClient) {
                        fetchMovements(selectedClient.id || selectedClient.documento_nro);
                    }
                    // Also refresh clients to update saldo_pendiente
                    fetchClients();
                }}
            />

            {/* Facturar Cuenta Abierta Modal */}
            <FacturarCuentaAbiertaModal
                open={facturarModalVisible}
                onClose={() => {
                    setFacturarModalVisible(false);
                    setSelectedItemsForInvoice([]);
                }}
                clientData={selectedClient ? {
                    id: selectedClient.id,
                    documento_tipo: selectedClient.document_type || selectedClient.documento_tipo || 'CUIT', // ✅ Required for AFIP
                    documento_nro: selectedClient.document_number || selectedClient.documento_nro || selectedClient.cuit,
                    razon_social: selectedClient.business_name || selectedClient.razon_social || selectedClient.nombre,
                    condicion_impositiva: selectedClient.tax_condition || selectedClient.condicion_impositiva || 'CONSUMIDOR FINAL',
                    condicion_iva: selectedClient.tax_condition_code || selectedClient.condicion_iva || 'CF',
                    direccion: selectedClient.address || selectedClient.direccion || ''
                } : null}
                selectedItems={selectedItemsForInvoice}
                onInvoiceGenerated={(invoiceData) => {
                    console.log('[CuentasAbiertas] Invoice generated:', invoiceData);
                    message.success(`Factura ${invoiceData.invoiceNumber || 'X'} generada exitosamente`);
                    // Refresh movements and clients
                    if (selectedClient) {
                        fetchMovements(selectedClient.id || selectedClient.documento_nro);
                    }
                    fetchClients();
                    setSelectedRowKeys([]);
                }}
                onClearSelection={() => {
                    setSelectedRowKeys([]);
                    setSelectedItemsForInvoice([]);
                }}
            />

            {/* Liquidar CARGOs Modal */}
            <LiquidarCargoModal
                visible={liquidarModalVisible}
                initialCargos={selectedCargosForSettlement}
                availableCredit={availableCredit}
                loading={settlementLoading}
                onConfirm={handleConfirmSettlement}
                onClose={() => setLiquidarModalVisible(false)}
            />

            {/* Void Movement Modal */}
            <Modal
                title={
                    <Space>
                        <ExclamationCircleOutlined style={{ color: '#faad14' }} />
                        <span>Anular Movimiento</span>
                    </Space>
                }
                open={voidModalVisible}
                onCancel={() => {
                    setVoidModalVisible(false);
                    setMovementToVoid(null);
                    setVoidReason('');
                }}
                onOk={handleVoidMovement}
                okText="Confirmar Anulación"
                cancelText="Cancelar"
                okButtonProps={{ 
                    danger: true, 
                    loading: voidLoading,
                    disabled: !voidReason || voidReason.trim().length < 5
                }}
                width={500}
            >
                {movementToVoid && (
                    <div>
                        <div style={{ 
                            background: '#fff7e6', 
                            border: '1px solid #ffd591', 
                            borderRadius: 8, 
                            padding: 16, 
                            marginBottom: 16 
                        }}>
                            <Text strong>⚠️ Esta acción no se puede deshacer</Text>
                            <br />
                            <Text type="secondary" style={{ fontSize: 12 }}>
                                Se creará un movimiento de reversión que anulará los efectos contables de este movimiento.
                            </Text>
                        </div>
                        
                        <div style={{ marginBottom: 16 }}>
                            <Text strong>Movimiento a anular:</Text>
                            <div style={{ 
                                background: '#fafafa', 
                                padding: 12, 
                                borderRadius: 6, 
                                marginTop: 8 
                            }}>
                                <div><Text type="secondary">ID:</Text> {movementToVoid.id}</div>
                                <div><Text type="secondary">Fecha:</Text> {formatDate(movementToVoid.fecha)}</div>
                                <div><Text type="secondary">Descripción:</Text> {movementToVoid.descripcion}</div>
                                <div><Text type="secondary">Monto:</Text> {formatPrice(movementToVoid.valor)}</div>
                                {movementToVoid.transactionId && (
                                    <div><Text type="secondary">Ref:</Text> {movementToVoid.transactionId}</div>
                                )}
                            </div>
                        </div>
                        
                        {/* Warning for invoiced items */}
                        {movementToVoid.items?.some(item => item.itemStatus === 'INVOICED' && item.canBeReinvoiced) && (
                            <div style={{ 
                                background: '#fff1f0', 
                                border: '1px solid #ffa39e', 
                                borderRadius: 8, 
                                padding: 12, 
                                marginBottom: 16 
                            }}>
                                <Text strong style={{ color: '#cf1322' }}>
                                    ⚠️ Este cargo tiene items facturados en facturas anuladas
                                </Text>
                                <br />
                                <Text type="secondary" style={{ fontSize: 12 }}>
                                    Al anular este cargo, los items quedarán como ANULADOS y no podrán ser re-facturados.
                                </Text>
                            </div>
                        )}
                        
                        <div>
                            <Text strong>Motivo de anulación: <Text type="danger">*</Text></Text>
                            <Input.TextArea
                                placeholder="Ingrese el motivo de la anulación (mínimo 5 caracteres)"
                                value={voidReason}
                                onChange={(e) => setVoidReason(e.target.value)}
                                rows={3}
                                maxLength={500}
                                showCount
                                style={{ marginTop: 8 }}
                                status={voidReason && voidReason.trim().length < 5 ? 'error' : ''}
                            />
                            {voidReason && voidReason.trim().length < 5 && (
                                <Text type="danger" style={{ fontSize: 12 }}>
                                    El motivo debe tener al menos 5 caracteres
                                </Text>
                            )}
                        </div>
                    </div>
                )}
            </Modal>

            {/* Sync-Debe Confirmation Modal */}
            <Modal
                title={
                    <Space>
                        <SyncOutlined style={{ color: '#fa8c16' }} />
                        <span>Actualizar DEBE al precio vigente</span>
                    </Space>
                }
                open={syncConfirmVisible}
                onCancel={() => {
                    if (!syncLoading) {
                        setSyncConfirmVisible(false);
                        setMovementToSync(null);
                    }
                }}
                onOk={handleSyncDebe}
                okText="Confirmar Actualización"
                cancelText="Cancelar"
                okButtonProps={{
                    loading: syncLoading,
                    style: { backgroundColor: '#fa8c16', borderColor: '#fa8c16' }
                }}
                width={520}
            >
                {movementToSync && (
                    <div>
                        <div style={{
                            background: '#fff7e6',
                            border: '1px solid #ffd591',
                            borderRadius: 8,
                            padding: 16,
                            marginBottom: 16
                        }}>
                            <Text strong>¿Actualizar el DEBE de este movimiento?</Text>
                            <br />
                            <Text type="secondary" style={{ fontSize: 12 }}>
                                El valor del DEBE se actualizará al precio de catálogo vigente. 
                                Todos los saldos posteriores de este cliente serán recalculados en cascada automáticamente.
                            </Text>
                        </div>

                        <div style={{ background: '#fafafa', padding: 12, borderRadius: 6, marginBottom: 16 }}>
                            <div style={{ marginBottom: 8 }}>
                                <Text type="secondary">Movimiento:</Text>{' '}
                                <Text strong>#{movementToSync.id}</Text>
                                {movementToSync.descripcion && (
                                    <span style={{ color: '#8c8c8c', marginLeft: 8, fontSize: 12 }}>
                                        {movementToSync.descripcion}
                                    </span>
                                )}
                            </div>
                            <div style={{ display: 'flex', gap: 32, alignItems: 'center' }}>
                                <div>
                                    <div style={{ fontSize: 11, color: '#8c8c8c', marginBottom: 2 }}>DEBE actual</div>
                                    <div style={{ fontSize: 16, fontWeight: 600, color: '#cf1322' }}>
                                        {formatPrice(movementToSync.debe)}
                                    </div>
                                </div>
                                <div style={{ fontSize: 20, color: '#fa8c16' }}>→</div>
                                <div>
                                    <div style={{ fontSize: 11, color: '#8c8c8c', marginBottom: 2 }}>Nuevo DEBE (precio vigente)</div>
                                    <div style={{ fontSize: 16, fontWeight: 600, color: '#fa8c16' }}>
                                        {formatPrice(movementToSync.updatedDebe)}
                                    </div>
                                </div>
                                <div style={{ marginLeft: 'auto' }}>
                                    <div style={{ fontSize: 11, color: '#8c8c8c', marginBottom: 2 }}>Variación</div>
                                    <div style={{
                                        fontSize: 13,
                                        fontWeight: 600,
                                        color: movementToSync.deviationDebe > 0 ? '#fa8c16' : '#52c41a'
                                    }}>
                                        {movementToSync.deviationDebe > 0 ? '+' : ''}{movementToSync.deviationDebe?.toFixed(2)}%
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div style={{
                            background: '#f6ffed',
                            border: '1px solid #b7eb8f',
                            borderRadius: 6,
                            padding: 12
                        }}>
                            <Text strong style={{ fontSize: 12 }}>📋 Nota histórica automática</Text>
                            <br />
                            <Text type="secondary" style={{ fontSize: 12 }}>
                                Se registrará en el campo de notas internas del movimiento:
                            </Text>
                            <div style={{
                                fontFamily: 'monospace',
                                fontSize: 11,
                                background: '#fff',
                                border: '1px solid #d9d9d9',
                                borderRadius: 4,
                                padding: '6px 10px',
                                marginTop: 6,
                                color: '#555'
                            }}>
                                DEBE ACTUALIZADO {new Date().toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: 'numeric' }).replace(/\//g, '-')}{' '}
                                DE {movementToSync.debe?.toFixed(2)} A {movementToSync.updatedDebe?.toFixed(2)}
                            </div>
                        </div>
                    </div>
                )}
            </Modal>

            {/* Pickup Management Modal */}
            <Modal
                title={
                    <Space>
                        <ShoppingCartOutlined style={{ color: '#52c41a' }} />
                        <span>Gestión de Retiro</span>
                    </Space>
                }
                open={pickupModalVisible}
                onCancel={() => {
                    setPickupModalVisible(false);
                    setMovementToPickup(null);
                    setPickupData({ pickedUpBy: '', affectsStock: true });
                }}
                onOk={handleUpdatePickup}
                okText={movementToPickup?.pickedUp ? 'Marcar como Pendiente' : 'Marcar como Retirado'}
                cancelText="Cancelar"
                okButtonProps={{ 
                    loading: pickupLoading,
                    type: movementToPickup?.pickedUp ? 'default' : 'primary'
                }}
                width={500}
            >
                {movementToPickup && (
                    <div>
                        <div style={{ marginBottom: 16 }}>
                            <Text strong>Movimiento:</Text>
                            <div style={{ 
                                background: '#fafafa', 
                                padding: 12, 
                                borderRadius: 6, 
                                marginTop: 8 
                            }}>
                                <div><Text type="secondary">Fecha:</Text> {formatDate(movementToPickup.fecha)}</div>
                                <div><Text type="secondary">Estado actual:</Text> {' '}
                                    <Tag color={movementToPickup.pickedUp ? 'green' : 'orange'}>
                                        {movementToPickup.pickedUp ? 'Retirado' : 'Pendiente'}
                                    </Tag>
                                </div>
                                {movementToPickup.pickedUpBy && (
                                    <div><Text type="secondary">Retirado por:</Text> {movementToPickup.pickedUpBy}</div>
                                )}
                            </div>
                        </div>
                        
                        {!movementToPickup.pickedUp && (
                            <>
                                <div style={{ marginBottom: 16 }}>
                                    <Text strong>Retirado por:</Text>
                                    <Input
                                        placeholder="Nombre de quien retiró (opcional)"
                                        value={pickupData.pickedUpBy}
                                        onChange={(e) => setPickupData({ ...pickupData, pickedUpBy: e.target.value })}
                                        style={{ marginTop: 8 }}
                                    />
                                </div>
                                
                                <div style={{ 
                                    background: '#e6f7ff', 
                                    border: '1px solid #91d5ff', 
                                    borderRadius: 8, 
                                    padding: 12 
                                }}>
                                    <Checkbox
                                        checked={pickupData.affectsStock}
                                        onChange={(e) => setPickupData({ ...pickupData, affectsStock: e.target.checked })}
                                    >
                                        <Text strong>Afectar stock</Text>
                                    </Checkbox>
                                    <br />
                                    <Text type="secondary" style={{ fontSize: 12, marginLeft: 24 }}>
                                        {pickupData.affectsStock 
                                            ? '✓ Se descontará el stock ahora' 
                                            : '⚠ Stock NO se descontará (se descontará al facturar)'}
                                    </Text>
                                </div>
                            </>
                        )}
                    </div>
                )}
            </Modal>

            {/* Ver Movimiento - Items Detail / Movement Detail Modal */}
            <Modal
                title={
                    <Space>
                        <EyeOutlined style={{ color: '#1890ff' }} />
                        <span>{movimientoToView?.tipo === 'MOVIMIENTO' ? 'Detalle del Movimiento' : 'Productos del Cargo'}</span>
                        {movimientoToView && (
                            <Text type="secondary" style={{ fontSize: 12, fontWeight: 'normal' }}>
                                {formatDate(movimientoToView.fecha)} — Ref: {movimientoToView.transactionId || movimientoToView.id}
                            </Text>
                        )}
                    </Space>
                }
                open={verMovimientoModalVisible}
                onCancel={() => {
                    setVerMovimientoModalVisible(false);
                    setMovimientoToView(null);
                }}
                footer={[
                    <Button key="close" type="primary" onClick={() => {
                        setVerMovimientoModalVisible(false);
                        setMovimientoToView(null);
                    }}>
                        Cerrar
                    </Button>
                ]}
                width={720}
            >
                {movimientoToView && (
                    <div>
                        {movimientoToView.tipo === 'MOVIMIENTO' ? (
                            /* ── MOVIMIENTO detail view ── */
                            (() => {
                                const tipoLabels = {
                                    'PAGO': 'Pago recibido',
                                    'ADELANTO': 'Adelanto / Seña',
                                    'AJUSTE_CREDITO': 'Ajuste a favor',
                                    'AJUSTE_FAVOR': 'Ajuste a favor',
                                    'AJUSTE_DEBITO': 'Ajuste en contra',
                                    'ANULACION': 'Anulación',
                                };
                                const metodoPagoLabels = {
                                    // Canonical backend values (SP contract)
                                    '201': 'Efectivo / Contado',
                                    'Efectivo': 'Efectivo / Contado',
                                    'CONTADO': 'Efectivo / Contado',
                                    'Transferencia': 'Transferencia bancaria',
                                    'Cheque': 'Cheque',
                                    'Tarjeta': 'Tarjeta',
                                    // Legacy values (old modal — kept for historical records)
                                    'EFECTIVO': 'Efectivo / Contado',
                                    'TRANSFERENCIA': 'Transferencia bancaria',
                                    'CHEQUE': 'Cheque',
                                    'TARJETA': 'Tarjeta',
                                    'TARJETA_DEBITO': 'Tarjeta de Débito',
                                    'TARJETA_CREDITO': 'Tarjeta de Crédito',
                                    'MERCADOPAGO': 'MercadoPago',
                                    'PAGO_ELECTRONICO': 'Pago Electrónico',
                                };
                                const isCredit = movimientoToView.haber > 0;
                                return (
                                    <div>
                                        {/* Type + accounting summary */}
                                        <div style={{
                                            background: isCredit ? '#f6ffed' : '#fff1f0',
                                            border: `1px solid ${isCredit ? '#b7eb8f' : '#ffa39e'}`,
                                            borderRadius: 8,
                                            padding: '12px 16px',
                                            marginBottom: 20,
                                            display: 'flex',
                                            gap: 32,
                                            flexWrap: 'wrap',
                                            alignItems: 'center'
                                        }}>
                                            <div>
                                                <Text type="secondary" style={{ fontSize: 12 }}>Tipo</Text>
                                                <div>
                                                    <Tag color={isCredit ? 'green' : 'red'} icon={<DollarOutlined />} style={{ marginTop: 2 }}>
                                                        {tipoLabels[movimientoToView.tipoOriginal] || movimientoToView.tipoOriginal || 'Movimiento'}
                                                    </Tag>
                                                </div>
                                            </div>
                                            {movimientoToView.debe > 0 && (
                                                <div>
                                                    <Text type="secondary" style={{ fontSize: 12 }}>DEBE</Text>
                                                    <div><Text strong style={{ color: '#cf1322', fontSize: 16 }}>{formatPrice(movimientoToView.debe)}</Text></div>
                                                </div>
                                            )}
                                            {movimientoToView.haber > 0 && (
                                                <div>
                                                    <Text type="secondary" style={{ fontSize: 12 }}>HABER</Text>
                                                    <div><Text strong style={{ color: '#52c41a', fontSize: 16 }}>{formatPrice(movimientoToView.haber)}</Text></div>
                                                </div>
                                            )}
                                            <div>
                                                <Text type="secondary" style={{ fontSize: 12 }}>Saldo tras movimiento</Text>
                                                <div><Text strong style={{ color: movimientoToView.saldo > 0 ? '#cf1322' : '#52c41a', fontSize: 16 }}>{formatPrice(movimientoToView.saldo)}</Text></div>
                                            </div>
                                        </div>

                                        {/* Detail rows */}
                                        <div style={{ display: 'grid', gridTemplateColumns: '160px 1fr', rowGap: 12, columnGap: 16 }}>
                                            <Text type="secondary">Fecha</Text>
                                            <Text strong>{formatDate(movimientoToView.fecha)}</Text>

                                            <Text type="secondary">Referencia</Text>
                                            <Text strong>{movimientoToView.transactionId || '-'}</Text>

                                            {movimientoToView.metodoPago && (
                                                <>
                                                    <Text type="secondary">Método de pago</Text>
                                                    <Text strong>
                                                        {metodoPagoLabels[movimientoToView.metodoPago] || movimientoToView.metodoPago}
                                                    </Text>
                                                </>
                                            )}

                                            {movimientoToView.referenciaPago && (
                                                <>
                                                    <Text type="secondary">N° Comprobante</Text>
                                                    <Text strong>{movimientoToView.referenciaPago}</Text>
                                                </>
                                            )}

                                            {movimientoToView.descripcion && (
                                                <>
                                                    <Text type="secondary">Descripción</Text>
                                                    <Text>{movimientoToView.descripcion}</Text>
                                                </>
                                            )}

                                            {movimientoToView.notasInternas && (
                                                <>
                                                    <Text type="secondary">Notas internas</Text>
                                                    <Text>{movimientoToView.notasInternas}</Text>
                                                </>
                                            )}

                                            <Text type="secondary">Estado</Text>
                                            <div>
                                                <Tag color={movimientoToView.status === 'VOIDED' ? 'default' : 'blue'} style={{ fontSize: 11 }}>
                                                    {movimientoToView.status === 'VOIDED' ? 'Anulado' : 'Activo'}
                                                </Tag>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })()
                        ) : (
                            /* ── CARGO detail view (items table) ── */
                            <>
                        {/* Summary row */}
                        <div style={{
                            background: '#f5f5f5',
                            borderRadius: 8,
                            padding: '10px 16px',
                            marginBottom: 16,
                            display: 'flex',
                            gap: 32,
                            flexWrap: 'wrap'
                        }}>
                            <div>
                                <Text type="secondary" style={{ fontSize: 12 }}>Total items</Text>
                                <div><Text strong>{movimientoToView.items.length}</Text></div>
                            </div>
                            <div>
                                <Text type="secondary" style={{ fontSize: 12 }}>Valor total</Text>
                                <div><Text strong style={{ color: '#cf1322' }}>{formatPrice(movimientoToView.debe || movimientoToView.valor)}</Text></div>
                            </div>
                            <div>
                                <Text type="secondary" style={{ fontSize: 12 }}>Estado</Text>
                                <div>
                                    {movimientoToView.tipo === 'PRODUCTO_FACTURADO'
                                        ? <Tag color="blue" icon={<FileTextOutlined />}>Facturado</Tag>
                                        : <Tag color="orange" icon={<ShoppingCartOutlined />}>Sin facturar</Tag>
                                    }
                                </div>
                            </div>
                            {movimientoToView.factura_nro && (
                                <div>
                                    <Text type="secondary" style={{ fontSize: 12 }}>Factura</Text>
                                    <div><Text strong>{movimientoToView.factura_nro}</Text></div>
                                </div>
                            )}
                        </div>

                        {/* Items table */}
                        <Table
                            dataSource={movimientoToView.items}
                            rowKey={(item, idx) => item.id || item.productCode || idx}
                            size="small"
                            pagination={false}
                            columns={[
                                {
                                    title: 'Código',
                                    dataIndex: 'productCode',
                                    key: 'productCode',
                                    width: 110,
                                    render: (code) => <Text code style={{ fontSize: 12 }}>{code || '-'}</Text>,
                                },
                                {
                                    title: 'Producto',
                                    dataIndex: 'productName',
                                    key: 'productName',
                                    render: (name) => <Text>{name || '-'}</Text>,
                                },
                                {
                                    title: 'Cant.',
                                    dataIndex: 'quantity',
                                    key: 'quantity',
                                    width: 60,
                                    align: 'center',
                                },
                                {
                                    title: 'Estado',
                                    dataIndex: 'itemStatus',
                                    key: 'itemStatus',
                                    width: 100,
                                    align: 'center',
                                    render: (status) => {
                                        const colors = { PENDING: 'orange', INVOICED: 'blue', VOIDED: 'default', CANCELLED: 'red' };
                                        const labels = { PENDING: 'Pendiente', INVOICED: 'Facturado', VOIDED: 'Anulado', CANCELLED: 'Cancelado' };
                                        return <Tag color={colors[status] || 'default'} style={{ fontSize: 11 }}>{labels[status] || status || '-'}</Tag>;
                                    },
                                },
                            ]}
                        />
                            </>
                        )}
                    </div>
                )}
            </Modal>

            {/* Saldo Client Info Modal */}
            <Modal
                title={
                    <Space>
                        <WalletOutlined style={{ color: '#722ed1' }} />
                        <span>Detalle del Saldo — {selectedClient?.business_name || selectedClient?.razon_social || selectedClient?.nombre || selectedClient?.displayName || 'Cliente'}</span>
                    </Space>
                }
                open={saldoInfoVisible}
                onCancel={() => setSaldoInfoVisible(false)}
                footer={[
                    <Button key="close" type="primary" onClick={() => setSaldoInfoVisible(false)}>
                        Entendido
                    </Button>
                ]}
                width={560}
            >
                <div>
                    {/* Explanation */}
                    <div style={{
                        background: '#f9f0ff',
                        border: '1px solid #d3adf7',
                        borderRadius: 8,
                        padding: 16,
                        marginBottom: 24
                    }}>
                        <Text strong>💡 ¿Cómo se calcula este saldo?</Text>
                        <br />
                        <Text type="secondary" style={{ fontSize: 13 }}>
                            El <strong>Saldo</strong> es la diferencia entre lo que el cliente debe (cargos) y lo que ya pagó o se le acreditó (haberes).
                        </Text>
                    </div>

                    {/* Breakdown rows */}
                    <div style={{ marginBottom: 8 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0', borderBottom: '1px solid #f0f0f0' }}>
                            <Text>Total Debe (precio de carga)</Text>
                            <Text strong style={{ color: '#cf1322', fontSize: 15 }}>$ {Math.round(totalDebe).toLocaleString('es-AR')}</Text>
                        </div>
                        {updatedTotalDebe != null && Math.abs(updatedTotalDebe - totalDebe) > 0.01 && (
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 0 10px 16px', borderBottom: '1px solid #f0f0f0' }}>
                                <Text type="secondary" style={{ fontSize: 12 }}>
                                    {updatedTotalDebe > totalDebe
                                        ? <RiseOutlined style={{ color: '#fa8c16', marginRight: 4 }} />
                                        : <FallOutlined style={{ color: '#52c41a', marginRight: 4 }} />
                                    }
                                    Total Debe a precios actuales
                                </Text>
                                <Text style={{ fontSize: 12, color: updatedTotalDebe > totalDebe ? '#fa8c16' : '#52c41a' }}>
                                    $ {Math.round(updatedTotalDebe).toLocaleString('es-AR')}
                                </Text>
                            </div>
                        )}
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0', borderBottom: '1px solid #f0f0f0' }}>
                            <Text>Total Haber (pagos y créditos)</Text>
                            <Text strong style={{ color: '#52c41a', fontSize: 15 }}>$ {Math.round(totalHaber).toLocaleString('es-AR')}</Text>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 0 0 0' }}>
                            <Text strong style={{ fontSize: 15 }}>Saldo actual</Text>
                            <Text strong style={{ fontSize: 18, color: saldoActual > 0 ? '#cf1322' : '#52c41a' }}>
                                $ {Math.round(saldoActual).toLocaleString('es-AR')}
                            </Text>
                        </div>
                    </div>

                    {/* Updated saldo section */}
                    {updatedSaldoActual != null && Math.abs(updatedSaldoActual - saldoActual) > 0.01 && (() => {
                        const isUp = saldoActualDeviation > 0;
                        const delta = updatedSaldoActual - saldoActual;
                        return (
                            <div style={{
                                marginTop: 20,
                                background: isUp ? '#fff7e6' : '#f6ffed',
                                border: `1px solid ${isUp ? '#ffd591' : '#b7eb8f'}`,
                                borderRadius: 8,
                                padding: 16
                            }}>
                                <Text strong style={{ fontSize: 14 }}>
                                    {isUp
                                        ? <RiseOutlined style={{ color: '#fa8c16', marginRight: 6 }} />
                                        : <FallOutlined style={{ color: '#52c41a', marginRight: 6 }} />
                                    }
                                    Saldo a precios actuales de productos
                                </Text>
                                <div style={{ marginTop: 8, fontSize: 12, color: '#8c8c8c' }}>
                                    Si los cargos pendientes se re-valuaran con los precios actuales de productos (mismos descuentos), el saldo sería:
                                </div>
                                <div style={{ marginTop: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <Text type="secondary">Saldo actualizado</Text>
                                    <Text strong style={{ fontSize: 16, color: isUp ? '#fa8c16' : '#52c41a' }}>
                                        $ {Math.round(updatedSaldoActual).toLocaleString('es-AR')}
                                    </Text>
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 6 }}>
                                    <Text type="secondary">Variación vs. precio de carga</Text>
                                    <Text style={{ color: isUp ? '#fa8c16' : '#52c41a' }}>
                                        {isUp ? '+' : '-'}$ {Math.round(Math.abs(delta)).toLocaleString('es-AR')}
                                        {saldoActualDeviation != null && (
                                            <span style={{ marginLeft: 4 }}>({isUp ? '+' : ''}{saldoActualDeviation.toFixed(2)}%)</span>
                                        )}
                                    </Text>
                                </div>
                                <div style={{ marginTop: 10, fontSize: 11, color: '#8c8c8c', borderTop: '1px solid #f0f0f0', paddingTop: 8 }}>
                                    💡 Los haberes (pagos, adelantos) no varían — solo cambian los cargos pendientes de facturar.
                                </div>
                            </div>
                        );
                    })()}
                </div>
            </Modal>

            {/* Balance Breakdown Modal */}
            <Modal
                title={
                    <Space>
                        <CalculatorOutlined style={{ color: '#1890ff' }} />
                        <span>Detalle del Saldo Total Adeudado</span>
                    </Space>
                }
                open={balanceBreakdownVisible}
                onCancel={() => setBalanceBreakdownVisible(false)}
                footer={[
                    <Button key="close" type="primary" onClick={() => setBalanceBreakdownVisible(false)}>
                        Entendido
                    </Button>
                ]}
                width={600}
            >
                {aggregateStats && (
                    <div>
                        {/* Explanation */}
                        <div style={{ 
                            background: '#e6f7ff', 
                            border: '1px solid #91d5ff',
                            borderRadius: 8,
                            padding: 16,
                            marginBottom: 24
                        }}>
                            <Text strong>💡 ¿Por qué este cálculo?</Text>
                            <br />
                            <Text type="secondary" style={{ fontSize: 13 }}>
                                El <strong>Saldo Total Adeudado</strong> incluye todos los cargos netos (después de descuentos), 
                                ajustes financieros y pagos aplicados. Este es el monto real que los clientes adeudan.
                            </Text>
                        </div>

                        {/* Breakdown for NEW API format */}
                        {aggregateStats?.invoicing && aggregateStats?.balanceBreakdown ? (
                            <div>
                                {/* Products Section */}
                                <div style={{ marginBottom: 24 }}>
                                    <Text strong style={{ fontSize: 15, display: 'block', marginBottom: 12 }}>
                                        📦 Productos Cargados ({aggregateStats.invoicing.pendingItems} items)
                                    </Text>
                                    
                                    <div style={{ paddingLeft: 16 }}>
                                        <div style={{ 
                                            display: 'flex', 
                                            justifyContent: 'space-between',
                                            marginBottom: 8
                                        }}>
                                            <Text type="secondary">Valor bruto (precio de carga)</Text>
                                            <Text>{formatPrice(aggregateStats.invoicing.pendingItemsGross || 0)}</Text>
                                        </div>
                                        
                                        <div style={{ 
                                            display: 'flex', 
                                            justifyContent: 'space-between',
                                            marginBottom: 8,
                                            color: '#52c41a'
                                        }}>
                                            <Text type="secondary">- Descuentos aplicados</Text>
                                            <Text style={{ color: '#52c41a' }}>
                                                -{formatPrice(aggregateStats.invoicing.pendingDiscountAmount || 0)}
                                            </Text>
                                        </div>
                                        
                                        <div style={{ 
                                            borderTop: '1px solid #d9d9d9',
                                            paddingTop: 8,
                                            marginTop: 8
                                        }}>
                                            <div style={{ 
                                                display: 'flex', 
                                                justifyContent: 'space-between'
                                            }}>
                                                <Text strong>Subtotal productos (neto)</Text>
                                                <Text strong style={{ color: '#1890ff' }}>
                                                    {formatPrice(aggregateStats.invoicing.pendingCargoNet || 0)}
                                                </Text>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Financial Adjustments Section */}
                                <div style={{ marginBottom: 24 }}>
                                    <Text strong style={{ fontSize: 15, display: 'block', marginBottom: 12 }}>
                                        💰 Ajustes Financieros
                                    </Text>
                                    
                                    <div style={{ paddingLeft: 16 }}>
                                        <div style={{ 
                                            display: 'flex', 
                                            justifyContent: 'space-between',
                                            marginBottom: 8
                                        }}>
                                            <Text type="secondary">+ Ajustes Débito (sin items)</Text>
                                            <Text style={{ color: '#cf1322' }}>
                                                +{formatPrice(aggregateStats.invoicing.financialAdjustmentsDebit || 0)}
                                            </Text>
                                        </div>
                                        
                                        <div style={{ 
                                            display: 'flex', 
                                            justifyContent: 'space-between',
                                            marginBottom: 8
                                        }}>
                                            <Text type="secondary">- Pagos/Adelantos recibidos</Text>
                                            <Text style={{ color: '#52c41a' }}>
                                                -{formatPrice(aggregateStats.invoicing.financialCredits || 0)}
                                            </Text>
                                        </div>
                                    </div>
                                </div>

                                {/* Total Section */}
                                <div style={{ 
                                    borderTop: '2px solid #cf1322',
                                    paddingTop: 16,
                                    background: '#fff1f0',
                                    padding: 16,
                                    borderRadius: 8
                                }}>
                                    <div style={{ 
                                        display: 'flex', 
                                        justifyContent: 'space-between',
                                        alignItems: 'center'
                                    }}>
                                        <Text strong style={{ fontSize: 16 }}>
                                            SALDO TOTAL ADEUDADO
                                        </Text>
                                        <Text strong style={{ fontSize: 20, color: '#cf1322' }}>
                                            {formatPrice(aggregateStats.balance?.total ?? aggregateStats.totalPendingBalance ?? 0)}
                                        </Text>
                                    </div>
                                    
                                    {/* Verification note */}
                                    {aggregateStats.balanceBreakdown && (
                                        <div style={{ marginTop: 12, fontSize: 12 }}>
                                            <Text type="secondary">
                                                ✓ Calculado: {formatPrice(aggregateStats.balanceBreakdown.calculatedTotal || 0)}
                                                {' | '}
                                                Real: {formatPrice(aggregateStats.balanceBreakdown.actualTotal || 0)}
                                                {Math.abs((aggregateStats.balanceBreakdown.calculatedTotal || 0) - (aggregateStats.balanceBreakdown.actualTotal || 0)) < 0.01 
                                                    ? ' ✓ Verificado' 
                                                    : ' ⚠️ Diferencia detectada'}
                                            </Text>
                                        </div>
                                    )}
                                </div>

                                {/* Updated Prices Section */}
                                {(() => {
                                    const aggTotal = aggregateStats?.balance?.total ?? 0;
                                    const aggUpdated = aggregateStats?.balance?.updatedTotal ?? null;
                                    if (aggUpdated == null || Math.abs(aggUpdated - aggTotal) < 0.01) return null;
                                    const aggDev = Math.abs(aggTotal) > 0.01 ? ((aggUpdated - aggTotal) / Math.abs(aggTotal) * 100) : null;
                                    const isUp = aggUpdated > aggTotal;
                                    const delta = aggUpdated - aggTotal;
                                    return (
                                        <div style={{
                                            marginTop: 16,
                                            background: isUp ? '#fff7e6' : '#f6ffed',
                                            border: `1px solid ${isUp ? '#ffd591' : '#b7eb8f'}`,
                                            borderRadius: 8,
                                            padding: 16
                                        }}>
                                            <Text strong style={{ fontSize: 14 }}>
                                                {isUp
                                                    ? <RiseOutlined style={{ color: '#fa8c16', marginRight: 6 }} />
                                                    : <FallOutlined style={{ color: '#52c41a', marginRight: 6 }} />
                                                }
                                                Precios de Productos Actualizados
                                            </Text>
                                            <div style={{ marginTop: 8, fontSize: 12, color: '#8c8c8c' }}>
                                                Si todos los cargos pendientes se actualizan (con los mismos descuentos aplicados), el saldo total sería:
                                            </div>
                                            <div style={{ marginTop: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                <Text type="secondary">Saldo a precios actuales</Text>
                                                <Text strong style={{ fontSize: 16, color: isUp ? '#fa8c16' : '#52c41a' }}>
                                                    $ {Math.round(aggUpdated).toLocaleString('es-AR')}
                                                </Text>
                                            </div>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 6 }}>
                                                <Text type="secondary">Variación vs. precio de carga</Text>
                                                <Text style={{ color: isUp ? '#fa8c16' : '#52c41a' }}>
                                                    {isUp ? '+' : '-'}$ {Math.round(Math.abs(delta)).toLocaleString('es-AR')}
                                                    {aggDev != null && (
                                                        <span style={{ marginLeft: 4 }}>({isUp ? '+' : ''}{aggDev.toFixed(1)}%)</span>
                                                    )}
                                                </Text>
                                            </div>
                                            <div style={{ marginTop: 10, fontSize: 11, color: '#8c8c8c', borderTop: '1px solid #f0f0f0', paddingTop: 8 }}>
                                                💡 Los créditos (pagos, adelantos, ajustes) no varían — solo se re-valúan los cargos pendientes de facturar.
                                            </div>
                                        </div>
                                    );
                                })()}
                            </div>
                        ) : (
                            /* Fallback for LEGACY API format */
                            <div>
                                <div style={{ 
                                    background: '#fffbe6', 
                                    border: '1px solid #ffe58f',
                                    borderRadius: 8,
                                    padding: 16,
                                    marginBottom: 16
                                }}>
                                    <Text type="secondary">
                                        ℹ️ Mostrando resumen simplificado. El backend aún no devuelve el desglose detallado.
                                    </Text>
                                </div>

                                <div style={{ marginBottom: 16 }}>
                                    <div style={{ 
                                        display: 'flex', 
                                        justifyContent: 'space-between',
                                        marginBottom: 12
                                    }}>
                                        <Text strong>Saldo Total Pendiente</Text>
                                        <Text strong style={{ fontSize: 18, color: '#cf1322' }}>
                                            {formatPrice(aggregateStats?.totalPendingBalance ?? aggregateStats?.saldos?.totalPendiente ?? 0)}
                                        </Text>
                                    </div>
                                    
                                    <div style={{ paddingLeft: 16 }}>
                                        <div style={{ 
                                            display: 'flex', 
                                            justifyContent: 'space-between',
                                            marginBottom: 8
                                        }}>
                                            <Text type="secondary">Items pendientes</Text>
                                            <Text>{aggregateStats?.pendingItems ?? aggregateStats?.facturacion?.itemsSinFacturar ?? 0} items</Text>
                                        </div>
                                        
                                        <div style={{ 
                                            display: 'flex', 
                                            justifyContent: 'space-between',
                                            marginBottom: 8
                                        }}>
                                            <Text type="secondary">Clientes activos</Text>
                                            <Text>{aggregateStats?.activeClients ?? aggregateStats?.clientes?.activos ?? 0} clientes</Text>
                                        </div>
                                    </div>
                                </div>

                                <div style={{ 
                                    background: '#f0f0f0',
                                    padding: 12,
                                    borderRadius: 8,
                                    marginTop: 16
                                }}>
                                    <Text type="secondary" style={{ fontSize: 12 }}>
                                        <strong>Nota:</strong> Para ver el desglose detallado (productos, descuentos, ajustes), 
                                        solicite al equipo de backend actualizar el endpoint /statistics a la nueva estructura.
                                    </Text>
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </Modal>
        </div>
    );
};

export default CuentasAbiertasTab;
